/* =========================================================================
   assets/rova.js — RovaSwim (rovaswim.ro)
   -------------------------------------------------------------------------
   Singurul fișier JavaScript al secțiunilor personalizate `rova-*` construite
   peste tema Pipeline 7.5.0 (Groupthought). Este generat prin asamblarea
   fișierelor din `js-parts/`; nu edita un modul aici fără să actualizezi și
   partea lui sursă.

   FIȘIER PROPRIU RovaSwim — NU FACE PARTE DIN TEMA PIPELINE.
   Pipeline nu livrează niciodată calea `assets/rova.js`, deci o actualizare de
   temă de la furnizor nu o poate suprascrie și nici nu o poate șterge. În
   procedura de actualizare (CORRECTIONS.md, §13.9) acest fișier este „clasa C —
   al nostru”: dacă apare ca modificat sau șters după despachetarea unei versiuni
   noi de Pipeline, despachetarea a fost făcută greșit — restaurează-l din git,
   nu accepta versiunea furnizorului.

   ÎNCĂRCARE
   `snippets/rova-assets.liquid` emite <script src="…rova.js" defer>. `defer`
   garantează execuția DUPĂ theme.js (tot `defer`, layout/theme.liquid:182), deci
   `window.theme`, `window.themeVendor`, Alpine și toate elementele custom există
   deja când rulăm. Snippetul poate fi randat de N ori pe pagină: `__rovaBooted`
   face din a doua execuție un no-op.

   INTERDICȚII (SPEC §2.4 și §10)
     * NICIODATĂ `register(...)` — este privat unui IIFE închis din theme.js și
       aruncă la tip duplicat.
     * NICIODATĂ `Alpine.data(...)` / `alpine:init` — `Alpine.start()` rulează
       înainte de atribuirea lui `window.Alpine` (theme.js:21097-21099).
     * Nicio variabilă globală în afara `window.__rovaBooted` și `window.Rova`.
     * Niciun `document.querySelector` în module: totul este ancorat în `root`.
       (`RM` și bootloader-ul sunt singurele excepții, prin proiectare.)
     * Orice ascultător pus pe `document` / `window` / MediaQueryList TREBUIE
       eliberat în `destroy()`, expus și ca `root.__rovaTeardown`.

   NIVEL DE LIMBAJ: ES5 strict pe sintaxă (var, function, fără arrow, fără
   `const`/`let`, fără template literals, fără spread, FĂRĂ optional chaining
   `?.` și fără `??`). API-urile moderne folosite (Promise, fetch,
   MutationObserver, IntersectionObserver, Element.closest, CustomEvent,
   matchMedia) sunt toate testate înainte de folosire sau au rezervă. theme.js
   este transpilat mai sus decât atât, dar rova.js nu trece prin niciun build:
   ce scriem aici ajunge neschimbat în browser, deci rămâne conservator.

   ORDINEA FIȘIERULUI (SPEC §10)
     1. gardă de boot + registru `M` + `RM` + boot()/ready() + ascultătorii
        editorului de teme
     2. M.reviewstack
     3. M.faq
     4. M.pdp
     5. M.cart
     6. M.cartupsell
     7. M.grid
     8. aliasuri de registru
     9. ready(boot) + exportul `window.Rova`

   CONTRACTUL REGISTRULUI
   Un modul este fie o funcție `init(rootEl, sectionId)`, fie un obiect
   `{ init: function (rootEl, sectionId), destroy: function (rootEl) }`.
   `boot()` acceptă ambele forme; toate modulele de mai jos sunt obiecte.
   ========================================================================= */
(function () {
  if (window.__rovaBooted) { return; }
  window.__rovaBooted = true;

  /* Registrul de module: numele din `data-rova-section` -> modul. */
  var M = {};

  /* =======================================================================
     AJUTOARE PARTAJATE (SPEC §2.5)
     ======================================================================= */

  var RM = {

    /* Oglindește theme.js:8206-8212. Nu presupune nimic: dacă `themeVendor` nu
       este încă acolo (rova.js încărcat manual, fără theme.js), întoarce
       valoarea brută în bani, ca apelantul să nu primească `undefined`. */
    money: function (cents) {
      var t = window.theme;
      var v = window.themeVendor;
      if (!v || !v.themeCurrency || !v.themeCurrency.formatMoney || !t) {
        return String(cents);
      }
      var out = v.themeCurrency.formatMoney(cents, t.moneyFormat);
      if (t.settings && t.settings.currency_code_enable && t.currencyCode) {
        return out + ' ' + t.currencyCode;
      }
      return out;
    },

    /* Taie „,00” / „.00” DOAR când zecimalele sunt exact zero. Folosit când
       setarea „Ascunde zecimalele nule” este activă, ca să obținem „219 lei”.
       Liquid produce deja forma corectă prin `money_without_trailing_zeros`, dar
       orice nod rescris ulterior din JS revine la „219,00 lei”:
       themeVendor.themeCurrency.formatMoney are două zecimale hardcodate
       (vendor.js:470). Operația este idempotentă. */
    trimZeros: function (root) {
      if (!root || !root.querySelectorAll) { return; }
      var nodes = root.querySelectorAll('[data-rova-trim], [data-button-price]');
      Array.prototype.forEach.call(nodes, function (n) {
        var was = n.textContent;
        var now = was.replace(/([0-9])[.,]00(?![0-9])/g, '$1');
        if (now !== was) { n.textContent = now; }
      });
    },

    /* Section Rendering API, în stil Dawn.
       Calea TREBUIE să fie `window.location.pathname`, NU `theme.routes.root_url`:
       id-urile de secțiune se rezolvă față de șablonul căii cerute, deci
       `/cart?section_id=main` întoarce secțiunea `main` din cart.json, în timp ce
       `/?section_id=main` ar întoarce o cu totul altă secțiune, din index.json. */
    fetchSection: function (sectionId) {
      var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId);
      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) {
          if (!r.ok) { throw new Error('HTTP ' + r.status); }
          return r.text();
        })
        .then(function (html) {
          var d = document.createElement('div');
          d.innerHTML = html;
          return d;
        });
    },

    /* Ține insigna nativă din header în sincron. */
    cartChange: function (cart) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        detail: { cart: cart },
        bubbles: true
      }));
    },

    /* Pluralul românesc cu 3 forme (CORRECTIONS, GL-9):
         n == 1                  -> `one`   („1 recenzie”)
         n % 100 între 1 și 19   -> `few`   („12 recenzii”)
         altfel                  -> `many`  („20 de recenzii”)
       Testul `n == 1` TREBUIE să fie primul: 1 satisface și `mod 1..19`.
       Liquid face același calcul pe server (snippets/rova-stars.liquid); funcția
       există pentru orice text recompus în browser. */
    romanianPlural: function (n, one, few, many) {
      var v = parseInt(n, 10);
      if (isNaN(v) || v < 0) { v = 0; }
      if (v === 1) { return one; }
      var mod = v % 100;
      if (mod >= 1 && mod <= 19) { return few; }
      return many;
    }
  };

  /* =======================================================================
     BOOTLOADER
     ======================================================================= */

  /* Apelează modulul potrivit pentru un singur element-rădăcină. */
  function initOne(el) {
    if (!el || el.nodeType !== 1) { return; }
    if (el.hasAttribute('data-rova-ready')) { return; }
    el.setAttribute('data-rova-ready', '');
    var name = el.getAttribute('data-rova-section');
    var mod = M[name];
    /* O secțiune fără modul (hero, trust, instagram, why, size-guide, reviews,
       product-row, whatsapp) este marcată și ignorată — comportament dorit. */
    if (!mod) { return; }
    var sectionId = el.getAttribute('data-section-id') ||
      (el.closest ? sectionIdFromWrapper(el) : null);
    try {
      if (typeof mod === 'function') { mod(el, sectionId); }
      else if (typeof mod.init === 'function') { mod.init(el, sectionId); }
    } catch (e) {
      if (window.console) { window.console.warn('[rova] ' + name + ' failed', e); }
    }
  }

  /* Rezervă pentru `data-section-id`: Shopify învelește fiecare secțiune în
     `<div id="shopify-section-{{ section.id }}">`. */
  function sectionIdFromWrapper(el) {
    var w = el.closest('[id^="shopify-section-"]');
    if (!w) { return null; }
    return w.id.replace(/^shopify-section-/, '');
  }

  function boot(scope) {
    var host = scope || document;
    /* `shopify:section:load` livrează chiar învelișul secțiunii, iar rădăcina
       poate fi el însuși sau un descendent — le acoperim pe amândouă. */
    if (host.nodeType === 1 && host.hasAttribute && host.hasAttribute('data-rova-section')) {
      initOne(host);
    }
    if (!host.querySelectorAll) { return; }
    Array.prototype.forEach.call(host.querySelectorAll('[data-rova-section]'), initOne);
  }

  function teardownOne(el) {
    if (!el || el.nodeType !== 1) { return; }
    var name = el.getAttribute('data-rova-section');
    var mod = M[name];
    try {
      if (mod && typeof mod.destroy === 'function') { mod.destroy(el); return; }
      if (typeof el.__rovaTeardown === 'function') { el.__rovaTeardown(); }
    } catch (e) {
      if (window.console) { window.console.warn('[rova] teardown ' + name + ' failed', e); }
    }
  }

  function unboot(scope) {
    var host = scope || document;
    if (host.nodeType === 1 && host.hasAttribute && host.hasAttribute('data-rova-section')) {
      teardownOne(host);
    }
    if (!host.querySelectorAll) { return; }
    Array.prototype.forEach.call(host.querySelectorAll('[data-rova-section]'), teardownOne);
  }

  function ready(fn) {
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', fn); }
    else { fn(); }
  }

  /* Editorul de teme: o secțiune re-randată sosește ca subarbore al lui
     event.target. La descărcare eliberăm TOATE ascultătoarele puse pe
     document / window / MediaQueryList, altfel se acumulează la fiecare
     modificare de setare. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { unboot(e.target); });

  /* =====================================================================
     INJECTOR DE BADGE-URI PE CARDURILE NATIVE PIPELINE
     Paginile de colecție/căutare folosesc grila Pipeline (<product-grid-item>),
     nu secțiunile Rova. Nu edităm fișiere ale furnizorului și nu putem scrie
     metacâmpuri, așa că punem badge-ul din JS — dar cu clasele NATIVE Pipeline
     (`product__badge product__badge--custom`), deci moștenește poziția și stilul
     temei, identic cu badge-urile ei de reducere / stoc epuizat.
     Sursa: window.__rovaBadges (snippets/rova-badge-data.liquid), handle → text.
     ===================================================================== */
  var rovaBadgeTimer = null;

  function rovaInjectBadges() {
    var map = window.__rovaBadges;
    if (!map) { return; }
    var items = document.querySelectorAll('product-grid-item');
    if (!items.length) { return; }
    Array.prototype.forEach.call(items, function (item) {
      if (item.getAttribute('data-rova-badged') === '1') { return; }
      /* Linkul imaginii = ancora care conține învelișul de imagine; exact acolo
         randează Pipeline însuși badge-ul, deci acolo îl punem și noi. */
      var imgWrap = item.querySelector('.product-grid-item__image-wrapper');
      var link = imgWrap ? imgWrap.closest('a[href*="/products/"]') : null;
      if (!link) { link = item.querySelector('a[href*="/products/"]'); }
      if (!link) { return; }
      /* Dacă Pipeline a pus deja un badge (reducere / stoc epuizat), nu-l dublăm. */
      if (link.querySelector('.product__badge')) { item.setAttribute('data-rova-badged', '1'); return; }
      var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (!m) { return; }
      var handle;
      try { handle = decodeURIComponent(m[1]); } catch (e) { handle = m[1]; }
      var text = map[handle];
      if (!text) { item.setAttribute('data-rova-badged', '1'); return; }
      var badge = document.createElement('div');
      badge.className = 'product__badge product__badge--custom rova-injected-badge';
      badge.textContent = text;
      link.appendChild(badge);
      item.setAttribute('data-rova-badged', '1');
    });
  }

  /* La încărcare, la re-randări din editor, și urmărind DOM-ul pentru carduri
     aduse prin AJAX (filtre de colecție, paginare infinită). Observatorul e
     debounce-uit, iar `data-rova-badged` împiedică reprocesarea și buclele. */
  ready(rovaInjectBadges);
  document.addEventListener('shopify:section:load', rovaInjectBadges);
  if (window.MutationObserver) {
    var rovaBadgeMO = new MutationObserver(function () {
      if (rovaBadgeTimer) { return; }
      rovaBadgeTimer = setTimeout(function () { rovaBadgeTimer = null; rovaInjectBadges(); }, 150);
    });
    ready(function () {
      if (document.body) { rovaBadgeMO.observe(document.body, { childList: true, subtree: true }); }
    });
  }



  /* ===================================================================
     M.reviewstack  (SPEC 4.4 - sections/rova-review-stack.liquid)
     =================================================================== */

  /* =========================================================================
     M.reviewstack — sections/rova-review-stack.liquid
     -------------------------------------------------------------------------
     Teanc de carduri foto în evantai. Cardul din față se poate glisa sau apăsa;
     teancul avansează automat la intervalul setat de comerciant.

     CONTRACTE
     * Toate interogările DOM sunt făcute DOAR în interiorul lui `root`. Nicio
       interogare la nivel de document (bug-ul machetei index_8.html, care lucra
       pe `document.getElementById('stack')` și ar fi spart două instanțe ale
       aceleiași secțiuni pe aceeași pagină).
     * Idempotent: a doua inițializare pe aceeași rădăcină iese imediat
       (`root.__rovaReviewstack` + atributul `data-rova-ready`).
     * `destroy(root)` oprește TOATE cronometrele, dezleagă TOATE ascultătorii
       (inclusiv cei de pe `document` și de pe MediaQueryList) și readuce
       cardurile la starea din HTML, ca reinițializarea să fie curată.
     * `prefers-reduced-motion: reduce` -> fără tranziții și fără avansare
       automată; schimbarea preferinței în timpul sesiunii este ascultată.
     * `Shopify.designMode` -> amestecarea ordinii este dezactivată (altfel
       previzualizarea din editor ar sări la fiecare re-randare), iar selectarea
       unui bloc aduce cardul respectiv în față și suspendă avansarea automată.

     FIXURI FAȚĂ DE MACHETĂ
     * `parseFloat(x) || fallback` transformă un 0 legitim în valoarea implicită;
       aici se testează explicit `isNaN`, deci „decalaj 0” chiar înseamnă 0.
     * durata tranziției de ieșire și `setTimeout`-ul de ieșire sunt derivate din
       aceeași constantă EXIT_MS, deci nu pot ajunge desincronizate.
     * un click care urmează unei glisări nu mai declanșează încă o avansare.
     ========================================================================= */
  M.reviewstack = {

    init: function (root) {
      if (!root || root.__rovaReviewstack) { return; }

      var deck = root.querySelector('[data-rova-deck]');
      if (!deck) { return; }

      var cards = Array.prototype.slice.call(deck.querySelectorAll('[data-rova-card]'));
      if (!cards.length) { return; }

      var nextBtn = root.querySelector('[data-rova-next]');
      var statusEl = root.querySelector('[data-rova-status]');

      var EXIT_MS = 380;
      var EXIT_S = (EXIT_MS / 1000) + 's';

      /* ---- configurare, exclusiv din atribute data-* ---------------------- */

      function attrNum(name, fallback) {
        var raw = root.getAttribute(name);
        if (raw === null || raw === '') { return fallback; }
        var v = parseFloat(raw);
        // 0 este o valoare validă (evantai fără decalaj), deci NU `|| fallback`.
        return isNaN(v) ? fallback : v;
      }
      function attrBool(name) { return root.getAttribute(name) === 'true'; }

      var cfg = {
        autoplay: attrBool('data-autoplay'),
        pauseHover: attrBool('data-pause-hover'),
        shuffle: attrBool('data-shuffle'),
        ms: attrNum('data-autoplay-ms', 120000),
        fling: attrNum('data-fling', 80),
        visible: attrNum('data-visible', 4),
        x: attrNum('data-fan-x', 52),
        y: attrNum('data-fan-y', 10),
        rot: attrNum('data-fan-rot', 5),
        scale: attrNum('data-fan-scale', 3) / 100
      };
      if (cfg.ms < 1000) { cfg.ms = 1000; }
      if (cfg.fling < 1) { cfg.fling = 80; }
      if (cfg.visible < 1) { cfg.visible = 1; }
      if (cfg.scale < 0) { cfg.scale = 0; }

      var designMode = !!(window.Shopify && window.Shopify.designMode);
      var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

      /* ---- stare ---------------------------------------------------------- */

      var state = {
        cards: cards,
        order: [],
        dragX: 0,
        sx: 0,
        pid: null,
        dragging: false,
        didDrag: false,
        leaving: false,
        reduce: !!(mq && mq.matches),
        paused: false,
        hoverPaused: false,
        focusPaused: false,
        hiddenPaused: false,
        selectPaused: false,
        autoTimer: null,
        exitTimer: null,
        listeners: [],
        mq: mq,
        mqLegacy: null
      };

      for (var i = 0; i < cards.length; i++) { state.order.push(i); }

      // Amestecarea este opt-in ȘI niciodată activă în editor: o previzualizare
      // care sare la fiecare re-randare face imposibilă alinierea machetei.
      if (cfg.shuffle && !designMode) {
        for (var a = state.order.length - 1; a > 0; a--) {
          var b = Math.floor(Math.random() * (a + 1));
          var tmp = state.order[a];
          state.order[a] = state.order[b];
          state.order[b] = tmp;
        }
      }

      /* ---- randare -------------------------------------------------------- */

      function render() {
        var order = state.order;
        var n = order.length;
        for (var k = 0; k < cards.length; k++) {
          var el = cards[k];
          var pos = order.indexOf(k);
          if (pos < 0) { pos = n; }
          var isFront = (pos === 0);

          el.style.zIndex = String(n - pos);

          if (isFront) {
            el.style.cursor = 'pointer';
            el.style.pointerEvents = '';
            if (state.leaving) {
              el.style.transition = state.reduce
                ? 'none'
                : 'transform ' + EXIT_S + ' ease, opacity ' + EXIT_S + ' ease';
              el.style.transform = 'translateX(130%) rotate(12deg)';
              el.style.opacity = '0';
            } else if (state.dragging) {
              el.style.transition = 'none';
              el.style.transform = 'translateX(' + state.dragX + 'px) rotate(' + (state.dragX * 0.045) + 'deg)';
              el.style.opacity = '1';
            } else {
              el.style.transition = state.reduce ? 'none' : 'transform .35s ease';
              el.style.transform = 'translateX(0) rotate(0deg)';
              el.style.opacity = '1';
            }
          } else {
            var dir = (pos % 2 === 1) ? 1 : -1;
            var sc = 1 - (pos * cfg.scale);
            if (sc < 0.2) { sc = 0.2; }
            el.style.cursor = 'default';
            // doar cardul din față primește gesturi; restul sunt pur decorative
            el.style.pointerEvents = 'none';
            el.style.transition = state.reduce ? 'none' : 'transform .35s ease, opacity .35s ease';
            el.style.transform = 'translateX(' + (dir * pos * cfg.x) + 'px) ' +
              'translateY(' + (pos * cfg.y) + 'px) ' +
              'rotate(' + (dir * pos * cfg.rot) + 'deg) scale(' + sc + ')';
            el.style.opacity = (pos >= cfg.visible) ? '0' : '1';
          }

          // Accesibilitate: doar cardul din față este expus tehnologiilor
          // asistive și doar linkul lui rămâne în ordinea de tabulare.
          if (isFront) { el.removeAttribute('aria-hidden'); }
          else { el.setAttribute('aria-hidden', 'true'); }

          var link = el.querySelector('[data-rova-card-link]');
          if (link) {
            if (isFront) { link.removeAttribute('tabindex'); }
            else { link.setAttribute('tabindex', '-1'); }
          }
        }
      }

      function announce() {
        if (!statusEl || !state.order.length) { return; }
        var tpl = root.getAttribute('data-status-template');
        if (!tpl) { return; }
        var current = state.order[0] + 1;
        statusEl.textContent = tpl
          .split('||i||').join(String(current))
          .split('||n||').join(String(cards.length));
      }

      /* ---- avansare automată ---------------------------------------------- */

      function schedule() {
        clearTimeout(state.autoTimer);
        state.autoTimer = null;
        if (!cfg.autoplay || state.reduce || state.paused) { return; }
        if (cards.length < 2) { return; }
        state.autoTimer = setTimeout(advance, cfg.ms);
      }

      function updatePaused() {
        state.paused = state.hoverPaused || state.focusPaused ||
          state.hiddenPaused || state.selectPaused;
        if (state.paused) {
          clearTimeout(state.autoTimer);
          state.autoTimer = null;
        } else {
          schedule();
        }
      }

      function advance() {
        if (state.leaving || cards.length < 2) { return; }
        schedule();

        // Nu ascundem niciodată (aria-hidden) un card care conține focusul.
        var front = cards[state.order[0]];
        if (front && document.activeElement && front.contains(document.activeElement) && nextBtn) {
          try { nextBtn.focus(); } catch (e) {}
        }

        state.leaving = true;
        state.dragging = false;
        // `didDrag` NU se resetează aici: el trebuie consumat de evenimentul
        // `click` care urmează glisării, altfel un card cu link ar naviga.
        render();

        clearTimeout(state.exitTimer);
        state.exitTimer = setTimeout(function () {
          state.exitTimer = null;
          state.order = state.order.slice(1).concat(state.order[0]);
          state.leaving = false;
          state.dragX = 0;
          render();
          announce();
        }, state.reduce ? 0 : EXIT_MS);
      }

      function bringToFront(idx) {
        var k = state.order.indexOf(idx);
        if (k < 0) { return; }
        clearTimeout(state.exitTimer);
        state.exitTimer = null;
        state.leaving = false;
        state.dragging = false;
        state.dragX = 0;
        if (k > 0) { state.order = state.order.slice(k).concat(state.order.slice(0, k)); }
        render();
        announce();
      }

      /* ---- gesturi (delegare pe deck; capture-ul de pointer retrimite
             pointermove/pointerup pe card, iar acestea bulează la deck) ------- */

      function frontCardFrom(target) {
        if (!target || !target.closest) { return null; }
        var el = target.closest('[data-rova-card]');
        if (!el) { return null; }
        var idx = cards.indexOf(el);
        if (idx < 0 || !state.order.length) { return null; }
        return (idx === state.order[0]) ? el : null;
      }

      function onPointerDown(e) {
        if (state.leaving) { return; }
        if (e.pointerType === 'mouse' && e.button !== 0) { return; }
        var el = frontCardFrom(e.target);
        if (!el) { return; }
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
        state.dragging = true;
        state.didDrag = false;
        state.dragX = 0;
        state.sx = e.clientX;
        state.pid = e.pointerId;
        render();
      }

      function onPointerMove(e) {
        if (!state.dragging || e.pointerId !== state.pid) { return; }
        state.dragX = e.clientX - state.sx;
        if (Math.abs(state.dragX) > 6) { state.didDrag = true; }
        render();
      }

      function onPointerUp(e) {
        if (!state.dragging || e.pointerId !== state.pid) { return; }
        var moved = Math.abs(state.dragX);
        state.dragging = false;
        state.pid = null;
        if (moved > cfg.fling) { advance(); }
        else { state.dragX = 0; render(); }
      }

      function onPointerCancel(e) {
        if (e.pointerId !== state.pid && state.pid !== null) { return; }
        state.dragging = false;
        state.dragX = 0;
        state.pid = null;
        render();
      }

      function onClick(e) {
        // Click-ul sintetizat după o glisare nu trebuie nici să avanseze încă o
        // dată, nici să deschidă linkul cardului. `didDrag` este setat exclusiv
        // în `pointermove` (peste 6px) și resetat exclusiv aici sau la începutul
        // interacțiunii următoare (`pointerdown`).
        if (state.didDrag) {
          state.didDrag = false;
          e.preventDefault();
          return;
        }
        var el = frontCardFrom(e.target);
        if (!el) { return; }
        // Dacă blocul are link, apăsarea pe card deschide linkul; avansarea
        // rămâne pe buton și pe glisare.
        if (e.target.closest && e.target.closest('[data-rova-card-link]')) { return; }
        if (state.leaving) { return; }
        advance();
      }

      function onNext() { advance(); }

      /* ---- pauze ---------------------------------------------------------- */

      function onEnter() { state.hoverPaused = true; updatePaused(); }
      function onLeave() { state.hoverPaused = false; updatePaused(); }
      function onFocusIn() { state.focusPaused = true; updatePaused(); }
      function onFocusOut() { state.focusPaused = false; updatePaused(); }
      function onVisibility() {
        state.hiddenPaused = !!document.hidden;
        updatePaused();
      }
      function onMotionChange() {
        state.reduce = !!(state.mq && state.mq.matches);
        render();
        schedule();
      }

      /* ---- editor de teme -------------------------------------------------- */

      function onBlockSelect(e) {
        if (!e.target || !root.contains(e.target)) { return; }
        var card = e.target.closest ? e.target.closest('[data-rova-card]') : null;
        if (!card) { return; }
        var idx = cards.indexOf(card);
        if (idx < 0) { return; }
        state.selectPaused = true;
        bringToFront(idx);
        updatePaused();
      }
      function onBlockDeselect(e) {
        if (!e.target || !root.contains(e.target)) { return; }
        state.selectPaused = false;
        updatePaused();
      }

      /* ---- legare ---------------------------------------------------------- */

      function on(target, type, fn, opts) {
        if (!target) { return; }
        target.addEventListener(type, fn, opts);
        state.listeners.push([target, type, fn, opts]);
      }

      on(deck, 'pointerdown', onPointerDown);
      on(deck, 'pointermove', onPointerMove);
      on(deck, 'pointerup', onPointerUp);
      on(deck, 'pointercancel', onPointerCancel);
      on(deck, 'lostpointercapture', onPointerCancel);
      on(deck, 'click', onClick);
      on(nextBtn, 'click', onNext);

      if (cfg.pauseHover) {
        // `mouseenter` fără `mouseleave` este un risc real pe touch (un tap
        // sintetizează un mouseenter care nu se mai retrage), deci legăm
        // handlerele de hover DOAR pe dispozitivele care chiar au hover.
        var canHover = !window.matchMedia || window.matchMedia('(hover: hover)').matches;
        if (canHover) {
          on(deck, 'mouseenter', onEnter);
          on(deck, 'mouseleave', onLeave);
        }
        // Focusul se urmărește întotdeauna: cine navighează la tastatură nu
        // trebuie să piardă cardul de sub degete.
        on(root, 'focusin', onFocusIn);
        on(root, 'focusout', onFocusOut);
      }

      on(document, 'visibilitychange', onVisibility);

      if (state.mq) {
        if (state.mq.addEventListener) {
          on(state.mq, 'change', onMotionChange);
        } else if (state.mq.addListener) {
          // Safari < 14 nu are addEventListener pe MediaQueryList.
          state.mq.addListener(onMotionChange);
          state.mqLegacy = onMotionChange;
        }
      }

      if (designMode) {
        on(document, 'shopify:block:select', onBlockSelect);
        on(document, 'shopify:block:deselect', onBlockDeselect);
      }

      /* ---- pornire --------------------------------------------------------- */

      root.__rovaReviewstack = state;
      root.setAttribute('data-rova-ready', '');

      // Compatibilitate cu ascultătorul `shopify:section:unload` din scheletul
      // rova.js, care apelează `el.__rovaTeardown()`.
      root.__rovaTeardown = function () { M.reviewstack.destroy(root); };

      state.hiddenPaused = !!document.hidden;
      render();
      updatePaused();
    },

    destroy: function (root) {
      var state = root && root.__rovaReviewstack;
      if (!state) { return; }

      clearTimeout(state.autoTimer);
      clearTimeout(state.exitTimer);
      state.autoTimer = null;
      state.exitTimer = null;

      for (var i = 0; i < state.listeners.length; i++) {
        var l = state.listeners[i];
        if (l[0] && l[0].removeEventListener) { l[0].removeEventListener(l[1], l[2], l[3]); }
      }
      state.listeners.length = 0;

      if (state.mq && state.mqLegacy && state.mq.removeListener) {
        state.mq.removeListener(state.mqLegacy);
      }
      state.mqLegacy = null;

      // Readucem cardurile la starea din HTML, ca o reinițializare să nu
      // moștenească transformări sau atribute ARIA vechi.
      for (var k = 0; k < state.cards.length; k++) {
        var el = state.cards[k];
        el.style.transform = '';
        el.style.transition = '';
        el.style.opacity = '';
        el.style.zIndex = '';
        el.style.cursor = '';
        el.style.pointerEvents = '';
        el.removeAttribute('aria-hidden');
        var link = el.querySelector('[data-rova-card-link]');
        if (link) { link.removeAttribute('tabindex'); }
      }

      root.removeAttribute('data-rova-ready');
      root.__rovaTeardown = null;
      try { delete root.__rovaReviewstack; }
      catch (e) { root.__rovaReviewstack = null; }
    }
  };


  /* ===================================================================
     M.faq  (SPEC 4.6 - sections/rova-faq.liquid)
     =================================================================== */

/* ---------------------------------------------------------------------------
   M.faq — sections/rova-faq.liquid (SPEC §4.6)

   Ce face acest modul, și ce NU face.

   NU construiește acordeonul: marcajul este <details>/<summary> nativ, deci
   tastatura, focusul, anunțarea stării de către cititoarele de ecran și
   funcționarea fără JavaScript vin de la browser. Modulul adaugă exact trei
   lucruri peste comportamentul nativ:

     1. animația de înălțime (0 <-> înălțimea reală a conținutului, apoi `auto`,
        ca răspunsurile lungi să nu fie NICIODATĂ tăiate — macheta folosea
        maxHeight:'320px', care trunchia în tăcere);
     2. exclusivitatea acordeonului cu animație pe AMBELE panouri;
     3. integrarea cu editorul de teme (selectarea unui bloc îl deschide).

   De ce controlăm click-ul în ambele sensuri, în loc să ascultăm doar `toggle`:
     * evenimentul `toggle` este pus în coadă (task asincron), deci între
       schimbarea atributului `open` și rularea handlerului poate exista un
       cadru desenat cu panoul la înălțime completă — un salt vizibil;
     * la ÎNCHIDERE, conținutul unui <details> închis nu mai este în arborele de
       casete, deci `scrollHeight` este 0 și animația de colapsare devine
       imposibilă. Amânăm ștergerea atributului `open` până la finalul animației.
     Enter/Space pe <summary> generează un eveniment `click`, deci handlerul
     delegat acoperă și tastatura.

   De ce scoatem atributul HTML `name`:
     `<details name="...">` dă acordeon exclusiv nativ, dar browserul închide
     vecinul INSTANT (conținutul lui dispare din arborele de casete în aceeași
     sarcină), deci panoul care iese nu poate fi animat. Cât timp JS-ul este
     activ scoatem atributul și impunem noi exclusivitatea, animând ambele
     panouri; `destroy()` îl pune la loc. În HTML atributul rămâne, deci fără
     JavaScript comportamentul exclusiv nativ este intact.

   Idempotență și curățenie:
     * steag per-element `data-rova-faq-ready` — un al doilea `init` iese imediat;
     * singurele ascultătoare în afara rădăcinii sunt `resize` pe window și
       `shopify:block:select` pe document; ambele sunt eliminate în `destroy()`,
       împreună cu temporizatoarele;
     * `destroy()` este expus și ca `root.__rovaTeardown`, pentru
       `shopify:section:unload` (SPEC §2.4).
   --------------------------------------------------------------------------- */
M.faq = {
  init: function (root) {
    if (!root || root.nodeType !== 1) { return; }
    if (root.hasAttribute('data-rova-faq-ready')) { return; }

    var i;
    var rows = Array.prototype.slice.call(root.querySelectorAll('.rova-faq__row'));
    var multiple = root.getAttribute('data-allow-multiple') === 'true';
    var designMode = !!(window.Shopify && window.Shopify.designMode);
    var groupName = '';
    var resizeTimer = null;

    var motionQuery = null;
    if (window.matchMedia) {
      try { motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)'); } catch (e) { motionQuery = null; }
    }
    /* Citit la fiecare animație, nu o singură dată la boot: preferința se poate
       schimba din sistem cu pagina deschisă. */
    function reduced() { return !!(motionQuery && motionQuery.matches); }

    function panelOf(row) { return row.querySelector('.rova-faq__panel'); }
    function bodyOf(panel) { return panel ? panel.firstElementChild : null; }

    /* --- gestiunea animației -------------------------------------------- */

    function cancelAnim(row) {
      if (row.__rovaFaqEnd) {
        var panel = panelOf(row);
        if (panel) { panel.removeEventListener('transitionend', row.__rovaFaqEnd); }
        row.__rovaFaqEnd = null;
      }
      if (row.__rovaFaqTimer) {
        clearTimeout(row.__rovaFaqTimer);
        row.__rovaFaqTimer = null;
      }
    }

    /* `transitionend` poate să nu se declanșeze deloc (filă în fundal, tranziție
       întreruptă, height identic), deci fiecare animație are și un temporizator
       de siguranță care duce starea la capăt. */
    function afterTransition(row, panel, done) {
      var handler = function (e) {
        if (e && e.target !== panel) { return; }
        if (e && e.propertyName && e.propertyName !== 'height') { return; }
        cancelAnim(row);
        done();
      };
      row.__rovaFaqEnd = handler;
      panel.addEventListener('transitionend', handler);
      row.__rovaFaqTimer = setTimeout(function () {
        cancelAnim(row);
        done();
      }, 700);
    }

    /* Citirea unei proprietăți de layout forțează reflow-ul, deci browserul are
       o valoare de start reală pentru tranziție. Rezultatul este folosit într-o
       condiție ca minificatoarele să nu poată elimina linia. */
    function reflow(el) {
      var h = el.offsetHeight;
      return h >= 0;
    }

    function expand(row) {
      var panel = panelOf(row);
      if (!panel) { return; }
      var body = bodyOf(panel);
      cancelAnim(row);
      if (reduced() || !body) {
        row.__rovaFaqDir = null;
        panel.style.height = 'auto';
        return;
      }
      panel.style.height = '0px';
      if (!reflow(panel)) { return; }
      panel.style.height = body.scrollHeight + 'px';
      afterTransition(row, panel, function () {
        row.__rovaFaqDir = null;
        /* `auto` după animație: dacă răspunsul își schimbă înălțimea (imagine
           încărcată târziu, redimensionare, zoom de text), panoul îl urmează. */
        if (row.open) { panel.style.height = 'auto'; }
      });
    }

    function collapse(row, done) {
      var panel = panelOf(row);
      if (!panel) { done(); return; }
      var body = bodyOf(panel);
      cancelAnim(row);
      if (reduced() || !body) {
        panel.style.height = '0px';
        done();
        return;
      }
      panel.style.height = body.scrollHeight + 'px';
      if (!reflow(panel)) { done(); return; }
      panel.style.height = '0px';
      afterTransition(row, panel, done);
    }

    /* --- deschidere / închidere ----------------------------------------- */

    /* `toggle` este asincron, deci nu putem folosi un steag boolean pus și
       șters sincron. Numărăm schimbările pe care le-am provocat noi și le
       consumăm una câte una în handlerul de `toggle`. `markPending` se apelează
       ÎNTOTDEAUNA imediat înaintea scrierii atributului `open`, deci raportul
       rămâne exact 1:1 chiar dacă utilizatorul dă click de mai multe ori în
       timpul unei animații.
       `__rovaFaqDir` reține direcția animației în curs ('open' / 'close' /
       null), ca un al doilea click să poată INVERSA animația, nu să o repornească
       în același sens. */
    function markPending(row) {
      row.__rovaFaqPending = (row.__rovaFaqPending || 0) + 1;
    }

    function closeOthers(row) {
      for (var j = 0; j < rows.length; j++) {
        if (rows[j] !== row && rows[j].open) { closeRow(rows[j]); }
      }
    }

    function openRow(row) {
      if (row.__rovaFaqDir === 'open') { return; }
      row.__rovaFaqDir = 'open';
      if (!multiple) { closeOthers(row); }
      if (!row.open) {
        markPending(row);
        row.open = true;
      }
      expand(row);
    }

    function closeRow(row) {
      if (!row.open) { return; }
      if (row.__rovaFaqDir === 'close') { return; }
      row.__rovaFaqDir = 'close';
      /* Atributul `open` se șterge abia la FINALUL animației: un <details>
         închis nu mai are conținut în arborele de casete, deci nu s-ar putea
         anima colapsarea. */
      collapse(row, function () {
        row.__rovaFaqDir = null;
        markPending(row);
        row.open = false;
        var panel = panelOf(row);
        if (panel) { panel.style.height = '0px'; }
      });
    }

    /* --- ascultătoare ---------------------------------------------------- */

    function rowFrom(node) {
      var el = node;
      while (el && el !== root) {
        if (el.classList && el.classList.contains('rova-faq__row')) {
          return rows.indexOf(el) === -1 ? null : el;
        }
        el = el.parentNode;
      }
      return null;
    }

    function onClick(e) {
      if (e.defaultPrevented) { return; }
      var node = e.target;
      var summary = null;
      while (node && node !== root) {
        if (node.classList && node.classList.contains('rova-faq__q')) { summary = node; break; }
        node = node.parentNode;
      }
      if (!summary) { return; }
      var row = rowFrom(summary);
      if (!row) { return; }
      /* Preluăm comutarea în ambele sensuri — vezi comentariul din antet. */
      e.preventDefault();
      var closing = row.__rovaFaqDir === 'close';
      if (row.open && !closing) { closeRow(row); } else { openRow(row); }
    }

    /* `toggle` NU se propagă în sus, deci este legat pe fiecare rând.
       Ajungem aici doar pentru schimbări pe care NU le-am provocat noi:
       acordeon exclusiv nativ (dacă atributul `name` a rămas), editor,
       extensii, restaurare de stare. */
    function onToggle(e) {
      var row = e.currentTarget;
      if (row.__rovaFaqPending > 0) {
        row.__rovaFaqPending--;
        return;
      }
      if (row.open) {
        if (!multiple) { closeOthers(row); }
        row.__rovaFaqDir = 'open';
        expand(row);
      } else {
        row.__rovaFaqDir = null;
        cancelAnim(row);
        var panel = panelOf(row);
        if (panel) { panel.style.height = '0px'; }
      }
    }

    /* La redimensionare, un panou deschis fixat pe o înălțime în px ar rămâne
       greșit dacă textul se re-așează pe alt număr de rânduri. `auto` este
       corect prin definiție și nu costă nimic. */
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        for (var j = 0; j < rows.length; j++) {
          if (!rows[j].open) { continue; }
          cancelAnim(rows[j]);
          var panel = panelOf(rows[j]);
          if (panel) { panel.style.height = 'auto'; }
        }
      }, 150);
    }

    /* Editorul de teme: selectarea unui bloc „Întrebare” din panoul din stânga
       trebuie să îl și deschidă, altfel comerciantul editează un răspuns pe
       care nu îl vede. Evenimentul se propagă, iar ținta este chiar elementul
       blocului (poartă block.shopify_attributes). */
    function onBlockSelect(e) {
      if (!e || !e.target || !root.contains(e.target)) { return; }
      var row = rowFrom(e.target);
      if (row && !row.open) { openRow(row); }
    }

    /* --- inițializare ---------------------------------------------------- */

    /* Ordinea contează: atributul activează regula CSS `height:0`, iar
       înălțimile inline se scriu în ACEEAȘI sarcină, deci nu există niciun
       cadru desenat în care panourile deschise să apară colapsate. */
    root.setAttribute('data-rova-faq-ready', '1');

    for (i = 0; i < rows.length; i++) {
      if (!multiple) {
        var nameAttr = rows[i].getAttribute('name');
        if (nameAttr) {
          groupName = nameAttr;
          rows[i].removeAttribute('name');
        }
      }
      rows[i].__rovaFaqPending = 0;
      rows[i].__rovaFaqDir = null;
      var p = panelOf(rows[i]);
      if (p) { p.style.height = rows[i].open ? 'auto' : '0px'; }
      rows[i].addEventListener('toggle', onToggle);
    }

    root.addEventListener('click', onClick);
    window.addEventListener('resize', onResize);
    if (designMode) { document.addEventListener('shopify:block:select', onBlockSelect); }

    function teardown() {
      clearTimeout(resizeTimer);
      resizeTimer = null;
      root.removeEventListener('click', onClick);
      window.removeEventListener('resize', onResize);
      if (designMode) { document.removeEventListener('shopify:block:select', onBlockSelect); }

      for (var j = 0; j < rows.length; j++) {
        var row = rows[j];
        row.removeEventListener('toggle', onToggle);
        cancelAnim(row);
        row.__rovaFaqPending = 0;
        row.__rovaFaqDir = null;
        var panel = panelOf(row);
        if (panel) { panel.style.height = ''; }
        /* punem la loc acordeonul exclusiv nativ */
        if (groupName && !row.getAttribute('name')) { row.setAttribute('name', groupName); }
      }

      root.removeAttribute('data-rova-faq-ready');
      root.__rovaFaqTeardown = null;
      if (root.__rovaTeardown === teardown) { root.__rovaTeardown = null; }
    }

    root.__rovaFaqTeardown = teardown;
    root.__rovaTeardown = teardown;
  },

  destroy: function (root) {
    if (!root || root.nodeType !== 1) { return; }
    if (typeof root.__rovaFaqTeardown === 'function') { root.__rovaFaqTeardown(); return; }
    if (typeof root.__rovaTeardown === 'function') { root.__rovaTeardown(); }
  }
};


  /* ===================================================================
     M.pdp  (SPEC 5.5 + 5.9 - sections/rova-product.liquid)
     =================================================================== */

/* ---------------------------------------------------------------------------
   M.pdp — sections/rova-product.liquid (SPEC §5.5, §5.9; CORRECTIONS „M.pdp”)

   PRINCIPIU: ZERO logică de coș aici.
   Elementul custom <product-form> din Pipeline face deja rezolvarea variantei,
   prețurile, marcarea stocului epuizat, starea butonului, istoricul URL și
   POST-ul către /cart/add.js. Modulul acesta adaugă exact patru lucruri:

     1. comutarea slide-urilor din galerie (Flickity este DEZACTIVAT prin
        data-slideshow-*-style="none", deci teancul de slide-uri este al nostru);
     2. tăierea zecimalelor „,00” după ce Pipeline rescrie prețurile;
     3. notița de accesorii, comutată între cele două texte ale comerciantului
        în funcție de disponibilitatea reală;
     4. bara mobilă fixă (proxy peste butonul real de „Adaugă în coș”).

   -------------------------------------------------------------------------
   CONTRACTUL DE TIMP — citește asta înainte să atingi orice de mai jos.
   assets/theme.js:7801-7817  ProductForm.pushState() rulează, în ordine:
      7810  fireHookEvent()        -> emite 'theme:variant:change'
      7812  this.sellout.update()  -> SINGURUL scriitor al claselor .sold-out /
                                      .unavailable (theme.js:7648-7653)
   dispatchEvent() este SINCRON, deci orice lucru făcut în interiorul
   ascultătorului vede clasele de stoc de la schimbarea ANTERIOARĂ. În temă nu
   există niciun eveniment de după actualizare.
   Un microtask este golit abia la pasul „perform a microtask checkpoint”, adică
   după ce s-a golit stiva de execuție — deci după tot lanțul sincron
   (change pe radio -> ProductFormReader._onFormEvent theme.js:7508-7513 ->
    onOptionChange -> pushState -> fireHookEvent -> sellout.update).
   Amânarea cu un microtask este deci GARANTAT corectă, mai ieftină și mai sigură
   decât un MutationObserver (care nu s-ar declanșa deloc atunci când mulțimea
   variantelor epuizate rămâne neschimbată).
   -------------------------------------------------------------------------

   IDEMPOTENȚĂ ȘI CURĂȚENIE
     * steag per-element `data-rova-pdp-ready` — un al doilea init iese imediat;
     * singurele ascultătoare în afara rădăcinii sunt `resize` pe window și
       `shopify:block:select` pe document (doar în editor); ambele sunt
       eliminate în destroy(), împreună cu IntersectionObserver-ul și
       temporizatoarele;
     * destroy() este expus și ca root.__rovaTeardown, pentru
       `shopify:section:unload` (SPEC §2.4);
     * `--rova-sticky-h` este scris pe document.documentElement (niciodată pe
       rădăcina secțiunii — o valoare acolo ar umbri valoarea moștenită de la
       :root și ridicarea bulei de WhatsApp ar înceta în tăcere) și este șters
       la destroy.

   MIȘCARE REDUSĂ
     Singura mișcare pe care o comandă JS-ul este derularea benzii de miniaturi;
     ea devine instantanee când vizitatorul a cerut mișcare redusă. Preferința
     este citită la FIECARE derulare, nu o dată la boot: se poate schimba din
     sistem cu pagina deschisă.

   NU FACE NICIODATĂ AICI
     * `document.querySelectorAll(...)` — orice interogare este ancorată în root
       (bug-ul din machetă: `document.querySelectorAll('.thumb')`);
     * `register(...)` (aruncă la tip duplicat) sau `Alpine.data(...)`
       (Alpine.start() rulează înainte de atribuirea lui window.Alpine);
     * vreun apel care schimbă valoarea inputului [name="id"] — aceea este
       exclusiv treaba lui ProductForm.
   --------------------------------------------------------------------------- */
M.pdp = {
  init: function (root) {
    if (!root || root.nodeType !== 1) { return; }
    if (root.hasAttribute('data-rova-pdp-ready')) { return; }
    root.setAttribute('data-rova-pdp-ready', '1');

    var trim = root.getAttribute('data-trim-prices') === 'true';
    var hideSoldOut = root.getAttribute('data-soldout-style') === 'hide';
    var designMode = !!(window.Shopify && window.Shopify.designMode);

    var motionQuery = null;
    if (window.matchMedia) {
      try { motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)'); } catch (e) { motionQuery = null; }
    }
    function reduced() { return !!(motionQuery && motionQuery.matches); }

    function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }

    /* `Element.closest` lipsește pe elementele SVG în browsere mai vechi, iar
       ținta unui click poate fi chiar un <svg> din interiorul unei miniaturi.
       Urcăm manual și ne oprim la rădăcina secțiunii — niciodată mai sus. */
    function closestIn(node, sel) {
      var el = node;
      while (el && el !== root) {
        if (el.nodeType === 1 && el.matches && el.matches(sel)) { return el; }
        el = el.parentNode;
      }
      return null;
    }

    /* =====================================================================
       1. GALERIE
       ===================================================================== */

    var stage = root.querySelector('[data-product-slideshow]');
    var slides = stage ? stage.querySelectorAll('[data-rova-slide]') : [];
    var thumbs = root.querySelectorAll('[data-slideshow-thumbnail]');
    var thumbsBox = root.querySelector('[data-thumbs-holder]');

    function slideFor(id) {
      var found = null;
      each(slides, function (sl) {
        if (!found && sl.getAttribute('data-media-id') === id) { found = sl; }
      });
      return found;
    }

    /* Derulăm DOAR containerul benzii, calculat din dreptunghiuri, nu prin
       scrollIntoView: acela poate derula și ancestorii, adică pagina, la
       fiecare schimbare de variantă. */
    function revealThumb(t) {
      if (!t || !thumbsBox || !thumbsBox.getBoundingClientRect) { return; }
      var cr = thumbsBox.getBoundingClientRect();
      var tr = t.getBoundingClientRect();
      var top = thumbsBox.scrollTop;
      var left = thumbsBox.scrollLeft;
      if (tr.top < cr.top) { top += tr.top - cr.top; }
      else if (tr.bottom > cr.bottom) { top += tr.bottom - cr.bottom; }
      if (tr.left < cr.left) { left += tr.left - cr.left; }
      else if (tr.right > cr.right) { left += tr.right - cr.right; }
      if (top === thumbsBox.scrollTop && left === thumbsBox.scrollLeft) { return; }
      if (thumbsBox.scrollTo) {
        try {
          thumbsBox.scrollTo({ top: top, left: left, behavior: reduced() ? 'auto' : 'smooth' });
          return;
        } catch (e) { /* semnătura cu obiect nu este suportată — cădem mai jos */ }
      }
      thumbsBox.scrollTop = top;
      thumbsBox.scrollLeft = left;
    }

    function show(mediaId) {
      if (mediaId === null || mediaId === undefined) { return; }
      var id = String(mediaId);
      /* Dacă id-ul nu are slide (varianta nu are imagine proprie), nu atingem
         nimic — altfel am ascunde toate slide-urile și galeria ar rămâne goală. */
      if (!slideFor(id)) { return; }
      each(slides, function (sl) {
        sl.classList.toggle('is-active', sl.getAttribute('data-media-id') === id);
      });
      var active = null;
      each(thumbs, function (t) {
        var on = t.getAttribute('data-media-select') === id;
        t.classList.toggle('is-active', on);
        if (on) { t.setAttribute('aria-current', 'true'); active = t; }
        else { t.removeAttribute('aria-current'); }
      });
      if (active) { revealThumb(active); }
    }

    /* Delegare pe rădăcină: miniaturile sunt <button>, deci Enter/Space produc
       tot un `click` și tastatura funcționează fără cod suplimentar. */
    function onRootClick(e) {
      /* Săgețile de galerie (desktop). `goRel` este declarată mai jos —
         declarațiile de funcții sunt ridicate, deci apelul este valid aici. */
      if (closestIn(e.target, '[data-rova-gallery-prev]')) { goRel(-1); return; }
      if (closestIn(e.target, '[data-rova-gallery-next]')) { goRel(1); return; }

      var t = closestIn(e.target, '[data-slideshow-thumbnail]');
      if (!t) { return; }
      show(t.getAttribute('data-media-select'));
    }

    /* =====================================================================
       1bis. DUPĂ „ADAUGĂ ÎN COȘ” — MERGEM LA PAGINA DE COȘ
       -----------------------------------------------------------------
       Pipeline deschide un panou „produs adăugat” (CartPopdown, theme.js:3053).
       Panoul se randează prin Section Rendering API din `api-product-popdown`
       și presupune că există un sertar de coș. Cu `cart_style: page` sertarul
       NU se randează (cart-drawer.liquid:20), iar `connectCloseButton`
       (theme.js:3076-3083) face `closer.addEventListener` fără gardă de nul —
       dacă butonul de închidere lipsește din conținutul randat, aruncă EXCEPȚIE
       imediat după ce a pus clasa `is-visible`. Rezultatul: un panou gol care
       nu se mai închide și nu pornește nici temporizatorul de auto-închidere.

       Nu reparăm un fișier al furnizorului și nu construim un al doilea coș:
       avem deja o pagină de coș completă (produse, prag de transport gratuit,
       sumar, finalizare). Interceptăm evenimentul ÎNAINTE de Pipeline și
       trimitem clientul acolo.

       De ce merge interceptarea: evenimentul este trimis pe
       `[data-product-add-popdown-wrapper]` cu `bubbles: true` (theme.js:20390),
       iar Pipeline îl ascultă pe `document` în faza de BULARE (theme.js:3115).
       Ascultătorul nostru este pe `document` în faza de CAPTURARE, deci rulează
       primul pe drumul în jos; `stopPropagation` oprește lanțul înainte să
       ajungă la el. */
    function onCartPopdown(e) {
      e.stopPropagation();
      var routes = window.theme && window.theme.routes;
      window.location.href = (routes && routes.cart) || '/cart';
    }

    /* --- glisare între fotografii (deget pe mobil, mouse pe desktop) -------
       Slide-urile se comută cu display:none/block, deci NU putem urmări
       degetul cu un transform; schimbăm slide-ul la depășirea pragului.
       `touch-action: pan-y` din rova.css lasă derularea verticală a paginii
       neatinsă și ne predă doar gesturile orizontale.
       Ordinea o luăm din MINIATURILE VIZIBILE, nu din toate slide-urile:
       la schimbarea variantei theme.js ascunde miniaturile care nu aparțin
       variantei, iar o glisare nu are voie să aducă înapoi o poză filtrată. */
    var SWIPE_MIN = 40;     /* px pe orizontală ca să conteze drept glisare */
    var SWIPE_RATIO = 1.2;  /* cât de dominant trebuie să fie orizontalul */
    var drag = null;
    var swallowClick = false;

    function mediaOrder() {
      var ids = [];
      each(thumbs, function (t) {
        if (t.classList.contains('hide')) { return; }
        var id = t.getAttribute('data-media-select');
        if (id) { ids.push(id); }
      });
      if (ids.length) { return ids; }
      /* Produs cu o singură imagine sau fără bandă de miniaturi. */
      each(slides, function (sl) {
        var id = sl.getAttribute('data-media-id');
        if (id) { ids.push(id); }
      });
      return ids;
    }

    function goRel(delta) {
      var ids = mediaOrder();
      if (ids.length < 2) { return; }
      var cur = -1, k;
      for (k = 0; k < ids.length; k++) {
        var sl = slideFor(ids[k]);
        if (sl && sl.classList.contains('is-active')) { cur = k; break; }
      }
      if (cur < 0) { cur = 0; }
      show(ids[(cur + delta + ids.length) % ids.length]);
    }

    function onStagePointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) { return; }
      drag = { x: e.clientX, y: e.clientY, id: e.pointerId };
    }

    function onStagePointerUp(e) {
      if (!drag || e.pointerId !== drag.id) { return; }
      var dx = e.clientX - drag.x;
      var dy = e.clientY - drag.y;
      drag = null;
      if (Math.abs(dx) < SWIPE_MIN) { return; }
      if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) { return; }
      /* Gestul s-a încheiat ca glisare: înghițim click-ul care urmează, altfel
         s-ar deschide lupa PhotoSwipe la ridicarea degetului. */
      swallowClick = true;
      setTimeout(function () { swallowClick = false; }, 0);
      goRel(dx < 0 ? 1 : -1);
    }

    function onStagePointerCancel() { drag = null; }

    function onStageClickCapture(e) {
      if (!swallowClick) { return; }
      e.preventDefault();
      e.stopPropagation();
    }

    function onStageKeyDown(e) {
      var k = e.key;
      if (k === 'ArrowRight') { e.preventDefault(); goRel(1); }
      else if (k === 'ArrowLeft') { e.preventDefault(); goRel(-1); }
    }

    /* =====================================================================
       2. NOTIȚA DE ACCESORII
       Comută între cele două texte ale comerciantului după disponibilitatea
       reală a valorilor din grup. Clasele .sold-out / .unavailable sunt scrise
       de theme.js pe INPUTURI (theme.js:7648-7653).
       ===================================================================== */

    function syncAccessoryNote() {
      var note = root.querySelector('[data-rova-acc-note]');
      if (!note) { return; }
      var group = closestIn(note, '.rova-opt');
      if (!group) { return; }
      var limited = group.querySelector('.rova-opt__input.sold-out, .rova-opt__input.unavailable') !== null;
      var html = limited ? note.getAttribute('data-note-limited') : note.getAttribute('data-note-default');
      if (html === null || html === undefined) { return; }
      if (note.innerHTML !== html) { note.innerHTML = html; }
    }

    /* =====================================================================
       3. „Variante epuizate: ascunse complet”
       Dacă valoarea bifată tocmai a fost ascunsă, mutăm selecția pe prima
       valoare încă disponibilă, ca formularul să rămână valid. Raportăm dacă
       am mutat ceva, ca runSync să poată limita numărul de treceri.
       ===================================================================== */

    function rescueHiddenSelection() {
      var moved = false;
      each(root.querySelectorAll('.rova-opt'), function (g) {
        var checked = g.querySelector('.rova-opt__input:checked');
        if (!checked || !checked.classList.contains('sold-out')) { return; }
        var alt = g.querySelector('.rova-opt__input:not(.sold-out):not(.unavailable)');
        if (alt && alt !== checked) {
          alt.checked = true;
          alt.dispatchEvent(new Event('change', { bubbles: true }));
          moved = true;
        }
      });
      return moved;
    }

    /* =====================================================================
       4. BARA MOBILĂ FIXĂ
       Butonul din bară este un PROXY: nu poartă data-add-to-cart și nu stă în
       <product-form>. updateAddToCartState atinge doar PRIMUL
       [data-buttons-wrapper] din secțiune (theme.js:7822), iar
       productAddButtonForm().init() se leagă de PRIMUL <button> din
       <product-form> — un buton în plus acolo ar deturna trimiterea.
       ===================================================================== */

    var bar = root.querySelector('[data-rova-sticky]');
    var mainBtn = root.querySelector('[data-buttons-wrapper] [data-add-to-cart]');
    var proxy = bar ? bar.querySelector('[data-rova-sticky-add]') : null;
    var stickyIO = null;
    var resizeTimer = null;

    /* FEEDBACKUL ESTE AL PROXY-ULUI, nu al butonului real.
       Pipeline scrie eroarea de adăugare în [data-error-display], nod care stă
       lângă butonul REAL — adică exact în afara ecranului atunci când bara
       mobilă este vizibilă. Un eșec de adăugare ar fi deci complet mut, iar o a
       doua apăsare ar trimite un al doilea POST, dublând cantitatea.
       Bara își ia deci singură feedbackul: stare „ocupat” pe proxy (aria-busy +
       disabled + etichetă schimbată) plus o regiune role="status" proprie, în
       care oglindim textul temei. Nodul de stare este CREAT din JS:
       sections/rova-product.liquid nu îl emite și nu avem voie să îl edităm. */
    var errDisplay = root.querySelector('[data-buttons-wrapper] [data-error-display]');
    var proxyStatus = null;
    var errObserver = null;
    var proxyBusy = false;
    var proxyTimer = null;
    /* Plasă de siguranță: dacă nu apare nici `theme:cart:change`, nici eroarea
       (rețeaua cade în axios.get din updateHeaderTotal, theme.js:20369), butonul
       trebuie totuși să redevină apăsabil. */
    var PROXY_BUSY_MS = 12000;

    if (bar) {
      proxyStatus = bar.querySelector('[data-rova-sticky-status]');
      if (!proxyStatus) {
        proxyStatus = document.createElement('p');
        proxyStatus.className = 'rova-sticky__status';
        proxyStatus.setAttribute('data-rova-sticky-status', '');
        proxyStatus.setAttribute('role', 'status');
        proxyStatus.setAttribute('aria-live', 'polite');
        proxyStatus.hidden = true;
        bar.appendChild(proxyStatus);
      }
    }

    function setStickyStatus(msg) {
      if (!proxyStatus) { return; }
      var s = msg ? String(msg).replace(/\s+/g, ' ').trim() : '';
      if (proxyStatus.textContent !== s) { proxyStatus.textContent = s; }
      proxyStatus.hidden = !s;
      /* Bara tocmai și-a schimbat înălțimea, deci tokenul se republică. */
      publishHeight();
    }

    /* `slideUp` (theme.js:4067-4092) doar ascunde nodul, nu îl și golește: un
       text rămas într-un [data-error-display] cu display:none NU mai este o
       eroare curentă. */
    function currentErrorText() {
      if (!errDisplay) { return ''; }
      if (errDisplay.style && errDisplay.style.display === 'none') { return ''; }
      return errDisplay.textContent.replace(/\s+/g, ' ').trim();
    }

    function onErrorMutation() {
      var msg = currentErrorText();
      setStickyStatus(msg);
      /* Tranziția în eroare încheie starea de așteptare. */
      if (msg) { endProxyBusy(); }
    }

    function startProxyBusy() {
      setStickyStatus('');
      proxyBusy = true;
      if (proxy) { proxy.setAttribute('aria-busy', 'true'); }
      syncStickyBar();
      clearTimeout(proxyTimer);
      proxyTimer = setTimeout(function () {
        proxyTimer = null;
        endProxyBusy();
      }, PROXY_BUSY_MS);
    }

    function endProxyBusy() {
      clearTimeout(proxyTimer);
      proxyTimer = null;
      if (!proxyBusy) { return; }
      proxyBusy = false;
      if (proxy) { proxy.removeAttribute('aria-busy'); }
      syncStickyBar();
    }

    /* Succesul se aude pe document: handleSuccess -> updateHeaderTotal emite
       `theme:cart:change`, iar cu coșul deschis se emite în plus
       `theme:cart:reload` (theme.js:20369-20388). */
    function onCartSettled() {
      setStickyStatus('');
      endProxyBusy();
    }

    function onProxyClick(e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      /* Gardă împotriva dublei trimiteri: o a doua apăsare cât timp prima
         cerere este în zbor ar adăuga produsul de două ori. */
      if (proxyBusy) { return; }
      if (!mainBtn || mainBtn.disabled) { return; }
      startProxyBusy();
      mainBtn.click();
    }

    function syncStickyBar() {
      if (!bar) { return; }
      if (proxy) { proxy.disabled = proxyBusy || !!(mainBtn && mainBtn.disabled); }

      var txt = root.querySelector('[data-buttons-wrapper] [data-add-to-cart-text]');
      var lbl = proxy ? proxy.querySelector('[data-rova-sticky-label]') : null;
      if (lbl) {
        if (proxyBusy) {
          /* Eticheta de așteptare. Comerciantul o poate suprascrie punând
             `data-busy` pe [data-rova-sticky-label]. */
          var busyTxt = lbl.getAttribute('data-busy') || 'Se adaugă…';
          if (lbl.textContent !== busyTxt) { lbl.textContent = busyTxt; }
        } else if (txt && mainBtn && mainBtn.disabled) {
          /* „Stoc epuizat” etc. — textul îl scrie Pipeline, îl oglindim. */
          lbl.textContent = txt.textContent.replace(/\s+/g, ' ').trim();
        } else {
          var def = lbl.getAttribute('data-default');
          if (def !== null && lbl.textContent !== def) { lbl.textContent = def; }
        }
      }

      var opts = [];
      each(root.querySelectorAll('.rova-opt__input:checked'), function (i) {
        if (i.value) { opts.push(i.value); }
      });
      var meta = bar.querySelector('[data-rova-sticky-meta]');
      if (meta) { meta.textContent = opts.join(' · '); }
      var sep = bar.querySelector('[data-rova-sticky-sep]');
      if (sep) { sep.hidden = opts.length === 0; }
    }

    /* Înălțimea barei este publicată ca `--rova-sticky-h` pe <html>, ca bula de
       WhatsApp (SPEC §4.7) să se ridice deasupra ei. Pe desktop bara este
       display:none, deci offsetHeight este 0 și nu se publică nimic.
       NU scriem NICIODATĂ în document.body.style: o secțiune nu are voie să
       distrugă stilul inline pus de altcineva. Rezerva de spațiu de sub bară se
       face declarativ, din `padding-bottom` pe `.rova-product` (rova.css), care
       citește același token. */
    function publishHeight() {
      if (!bar) { return; }
      var h = bar.classList.contains('is-visible') ? bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--rova-sticky-h', h + 'px');
    }

    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(publishHeight, 120);
    }

    /* =====================================================================
       5. SINCRONIZAREA AMÂNATĂ CU UN MICROTASK (vezi contractul de timp)
       ===================================================================== */

    var syncQueued = false;
    var rescuePasses = 0;
    /* Un microtask pus la coadă nu poate fi anulat; steagul îl face inofensiv
       dacă secțiunea a fost desfăcută între timp (editor de teme). */
    var destroyed = false;

    function runSync() {
      syncQueued = false;
      if (destroyed) { return; }
      if (trim && typeof RM !== 'undefined' && RM.trimZeros) { RM.trimZeros(root); }
      syncAccessoryNote();
      if (hideSoldOut) {
        /* rescueHiddenSelection() emite `change`, ceea ce reintră în pushState
           și pune la coadă încă un runSync. Converge (o valoare care nu este
           .sold-out în starea ipotetică rămâne nemarcată și după ce chiar este
           bifată — theme.js:7634-7647), dar limităm oricum. */
        if (rescuePasses < 4 && rescueHiddenSelection()) { rescuePasses++; }
        else { rescuePasses = 0; }
      }
      syncStickyBar();
      publishHeight();
    }

    function scheduleSync() {
      if (syncQueued) { return; }
      syncQueued = true;
      if (window.Promise) { Promise.resolve().then(runSync); }
      else { setTimeout(runSync, 0); }
    }

    /* bubbles:true (theme.js:8107) de pe <product-form>, deci rădăcina aude. */
    function onVariantChange(e) {
      var v = e && e.detail && e.detail.variant;
      /* Galeria nu depinde de clasele de stoc, deci se poate face imediat. */
      if (v && v.featured_media) { show(v.featured_media.id); }
      scheduleSync();
    }

    /* =====================================================================
       6. EDITORUL DE TEME
       Selectarea unui bloc „Acordeon” din panoul din stânga trebuie să îl și
       deschidă, altfel comerciantul editează un text pe care nu îl vede.
       Evenimentul se propagă, iar ținta este chiar elementul blocului (poartă
       block.shopify_attributes).
       ===================================================================== */

    function onBlockSelect(e) {
      if (!e || !e.target || !root.contains(e.target)) { return; }
      var acc = closestIn(e.target, '.rova-pdp__acc');
      if (acc && !acc.open) { acc.open = true; }
    }

    /* =====================================================================
       7. LEGARE
       ===================================================================== */

    root.addEventListener('click', onRootClick);
    root.addEventListener('theme:variant:change', onVariantChange);
    window.addEventListener('resize', onResize);
    /* `true` = fază de capturare: obligatoriu, altfel Pipeline apucă primul. */
    document.addEventListener('theme:cart:popdown', onCartPopdown, true);
    if (designMode) { document.addEventListener('shopify:block:select', onBlockSelect); }

    /* Glisarea are rost doar cu cel puțin două fotografii. `tabindex` se pune
       din JS, nu din Liquid: fără ascultătorul de tastatură un `tabindex` în
       markup ar fi doar o oprire moartă în ordinea de tabulare. */
    var stageSwipe = stage && mediaOrder().length > 1;
    if (stageSwipe) {
      stage.addEventListener('pointerdown', onStagePointerDown);
      stage.addEventListener('pointerup', onStagePointerUp);
      stage.addEventListener('pointercancel', onStagePointerCancel);
      stage.addEventListener('click', onStageClickCapture, true);
      stage.addEventListener('keydown', onStageKeyDown);
      stage.classList.add('rova-pdp__stage--swipe');
      if (!stage.hasAttribute('tabindex')) { stage.setAttribute('tabindex', '0'); }
    }

    if (proxy) {
      proxy.addEventListener('click', onProxyClick);
      /* Ambele sunt emise pe `document`, deci se eliberează în teardown. */
      document.addEventListener('theme:cart:change', onCartSettled);
      document.addEventListener('theme:cart:reload', onCartSettled);
      if (errDisplay && window.MutationObserver) {
        /* `attributes`/`style`: slideDown/slideUp comută display-ul, deci și
           dispariția erorii este o mutație pe care trebuie să o oglindim. */
        errObserver = new MutationObserver(onErrorMutation);
        errObserver.observe(errDisplay, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['style']
        });
      }
    }

    if (bar) {
      var afterScroll = root.getAttribute('data-sticky-after-scroll') === 'true';
      if (afterScroll && window.IntersectionObserver && mainBtn) {
        stickyIO = new IntersectionObserver(function (entries) {
          bar.classList.toggle('is-visible', !entries[0].isIntersecting);
          publishHeight();
        }, { rootMargin: '0px 0px -40px 0px' });
        stickyIO.observe(mainBtn);
      } else {
        bar.classList.add('is-visible');
      }
      publishHeight();
    }

    /* Prima randare: theme.js și rova.js sunt ambele `defer`
       (layout/theme.liquid:181-182 + snippets/rova-assets.liquid), iar
       customElements.define('product-form', ...) rulează în timpul execuției
       lui theme.js (theme.js:8827-8829). Deci ProductForm.connectedCallback ->
       linkForm() -> pushState(state, true) (theme.js:7767, :7785) a rulat DEJA
       și clasele de stoc există în momentul în care bootează M.pdp. */
    scheduleSync();

    /* =====================================================================
       8. DESFACERE
       ===================================================================== */

    function teardown() {
      if (destroyed) { return; }
      destroyed = true;
      clearTimeout(resizeTimer);
      resizeTimer = null;
      clearTimeout(proxyTimer);
      proxyTimer = null;
      proxyBusy = false;

      root.removeEventListener('click', onRootClick);
      root.removeEventListener('theme:variant:change', onVariantChange);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('theme:cart:popdown', onCartPopdown, true);
      if (designMode) { document.removeEventListener('shopify:block:select', onBlockSelect); }

      if (stageSwipe) {
        stage.removeEventListener('pointerdown', onStagePointerDown);
        stage.removeEventListener('pointerup', onStagePointerUp);
        stage.removeEventListener('pointercancel', onStagePointerCancel);
        stage.removeEventListener('click', onStageClickCapture, true);
        stage.removeEventListener('keydown', onStageKeyDown);
        stage.classList.remove('rova-pdp__stage--swipe');
      }
      drag = null;
      swallowClick = false;

      if (proxy) {
        proxy.removeEventListener('click', onProxyClick);
        document.removeEventListener('theme:cart:change', onCartSettled);
        document.removeEventListener('theme:cart:reload', onCartSettled);
      }
      if (errObserver) { errObserver.disconnect(); errObserver = null; }

      /* Nodul de stare este creat de noi, deci tot noi îl scoatem. */
      if (proxyStatus && proxyStatus.parentNode) {
        proxyStatus.parentNode.removeChild(proxyStatus);
      }
      proxyStatus = null;

      if (stickyIO) { stickyIO.disconnect(); stickyIO = null; }

      /* Scris pe un element din AFARA secțiunii, deci trebuie curățat explicit —
         altfel rămâne după o reîncărcare de secțiune. */
      document.documentElement.style.removeProperty('--rova-sticky-h');

      root.removeAttribute('data-rova-pdp-ready');
      root.__rovaPdpTeardown = null;
      if (root.__rovaTeardown === teardown) { root.__rovaTeardown = null; }
    }

    root.__rovaPdpTeardown = teardown;
    root.__rovaTeardown = teardown;
  },

  destroy: function (root) {
    if (!root || root.nodeType !== 1) { return; }
    if (typeof root.__rovaPdpTeardown === 'function') { root.__rovaPdpTeardown(); return; }
    if (typeof root.__rovaTeardown === 'function') { root.__rovaTeardown(); }
  }
};


  /* ===================================================================
     M.cart  (SPEC 7.1 - sections/rova-cart.liquid)
     =================================================================== */

/* =========================================================================
   M.cart — sections/rova-cart.liquid                    (SPEC §7.1, §10 pt. 6)
   -------------------------------------------------------------------------
   Rădăcina: [data-rova-section="cart"], adică .rova.rova-cart.

   CE FACE
     1. Schimbă cantitatea unei linii și șterge linii prin /cart/change.js,
        fără reîncărcarea paginii.
     2. Re-randează secțiunea prin Section Rendering API
        (GET <pathname>?section_id=<section.id>) și înlocuiește innerHTML-ul
        nodului [data-rova-inner]. Avantajul față de partialul nativ
        `api-cart-items`: marcajul reîmprospătat păstrează TOATE setările
        secțiunii (etichete, praguri, comutatoare), pe care un partial fără
        schemă nu le poate vedea.
     3. Detectează limitarea tăcută de stoc și afișează mesajul potrivit.
     4. Ține în sincron insigna nativă din header (`theme:cart:change`) și se
        resincronizează când altcineva modifică coșul (sertarul Pipeline).
     5. Anunță celelalte secțiuni rova (upsell-ul) prin `rova:cart:refresh`.

   REGULA CARE NU SE NEGOCIAZĂ (CORRECTIONS, defect 12)
     /cart/change.js este un endpoint „200 cu limitare tăcută”. La succes
     întoarce ÎNTREGUL obiect coș, nu linia. Când cantitatea cerută depășește
     stocul, statusul rămâne 200 și singura diferență este că
     `items[n].quantity` este valoarea limitată. Prin urmare succesul NU se
     deduce niciodată din `response.ok`: recitim linia din `items` DUPĂ `key`
     și comparăm cantitatea ei cu întregul trimis.

       cerut | linia găsită după key | line.quantity | rezultat
       ------+-----------------------+---------------+---------------------
         0   | nu                    | –             | succes (ștergere)
        >0   | nu                    | –             | err_gone
        >0   | da                    | == cerut      | succes
        >0   | da                    | 0             | err_gone
        >0   | da                    | 0 < q < cerut | err_stock (||qty|| -> q)

     Non-2xx (404 „Cannot find variant”, 422 reguli de cantitate) => afișăm
     `description`, altfel `message`, altfel err_generic.

   A DOUA REGULĂ: mesajul se afișează DOAR după ce DOM-ul a fost re-randat, și
   DOAR în [data-rova-cart-error], care este FRATE cu [data-rova-inner]. Un
   mesaj pus în interior ar fi șters de `live.innerHTML = fresh.innerHTML`.

   IDEMPOTENȚĂ
     `init` poate fi apelat de oricâte ori: iese imediat dacă rădăcina are deja
     starea internă sau marcajul `data-rova-ready="cart"`. Nu presupune nimic
     despre valoarea pusă de bootloader — doar valoarea proprie („cart”)
     înseamnă „inițializat”.

   MIȘCARE REDUSĂ
     Modulul nu animează nimic: singurele două animații (umplerea barei de
     transport, bara de încărcare a rândului) sunt CSS și sunt deja anulate în
     `@media (prefers-reduced-motion: reduce)` din rova.css. Preferința este
     citită aici doar pentru derularea către mesajul de eroare.

   Shopify.designMode
     * nu mutăm focusul în editorul de teme (ar fura selecția blocului);
     * `shopify:section:load` reinițializează modulul, iar
       `shopify:section:unload` apelează root.__rovaTeardown() -> destroy();
     * re-randarea prin Section Rendering API folosește setările SALVATE, iar
       editorul salvează la fiecare modificare, deci nu există desincronizare.

   FĂRĂ JAVASCRIPT
     Marcajul funcționează și singur: `updates[]`, ancora de ștergere către
     /cart/change și butonul <noscript> „Actualizează”. Acest modul este un
     strat de confort, nu poarta de acces.
   ========================================================================= */
M.cart = {

	init: function (root, sectionId) {
		if (!root || root.nodeType !== 1) { return; }
		if (root.__rovaCart) { return; }
		if (root.getAttribute('data-rova-ready') === 'cart') { return; }
		root.setAttribute('data-rova-ready', 'cart');

		var self = this;
		var state = {
			sectionId: sectionId || root.getAttribute('data-section-id') || '',
			busy: false,
			design: !!(window.Shopify && window.Shopify.designMode),
			extTimer: null,
			onClick: null,
			onChange: null,
			onKeydown: null,
			onExternal: null
		};
		root.__rovaCart = state;

		/* Punte către bootloader-ul din SPEC §2.4, care apelează
		   root.__rovaTeardown() la `shopify:section:unload`. */
		root.__rovaTeardown = function () { self.destroy(root); };

		/* ---------------------------------------------------------------
		   Utilitare mici
		   --------------------------------------------------------------- */
		function warn(msg, e) {
			if (window.console && window.console.warn) { window.console.warn('[rova] ' + msg, e); }
		}

		function closest(el, sel) {
			if (!el) { return null; }
			if (el.closest) { return el.closest(sel); }
			var node = el;
			while (node && node.nodeType === 1) {
				if (node.matches && node.matches(sel)) { return node; }
				node = node.parentNode;
			}
			return null;
		}

		function txt(attr, fallback) {
			var v = root.getAttribute(attr);
			return (v && v.length) ? v : fallback;
		}

		function reducedMotion() {
			return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
		}

		function cartRoute() {
			if (window.theme && window.theme.routes && window.theme.routes.cart) {
				return window.theme.routes.cart;
			}
			return '/cart';
		}

		/* Prețurile: Liquid emite deja forma corectă prin
		   `money_without_trailing_zeros`, dar orice nod rescris ulterior din JS
		   revine la „219,00 lei” (themeVendor.themeCurrency.formatMoney are două
		   zecimale hardcodate, vendor.js:470). Operația este idempotentă. */
		function trimPrices() {
			if (root.getAttribute('data-trim-prices') !== 'true') { return; }
			if (typeof RM !== 'undefined' && RM && typeof RM.trimZeros === 'function') {
				RM.trimZeros(root);
				return;
			}
			var nodes = root.querySelectorAll('[data-rova-trim], [data-button-price]');
			Array.prototype.forEach.call(nodes, function (node) {
				var was = node.textContent;
				var now = was.replace(/([0-9])[.,]00(?![0-9])/g, '$1');
				if (now !== was) { node.textContent = now; }
			});
		}

		/* ---------------------------------------------------------------
		   Caseta de eroare — trăiește în afara lui [data-rova-inner].
		   --------------------------------------------------------------- */
		function errBox() { return root.querySelector('[data-rova-cart-error]'); }

		function showError(msg) {
			var box = errBox();
			if (!box || !msg) { return; }
			box.textContent = msg;
			box.hidden = false;
			if (box.__rovaT) { clearTimeout(box.__rovaT); }
			box.__rovaT = setTimeout(function () {
				box.hidden = true;
				box.textContent = '';
				box.__rovaT = null;
			}, 8000);
			/* Coșul poate fi derulat sub linia de plutire; aducem mesajul în
			   câmpul vizual, dar niciodată brusc dacă utilizatorul a cerut
			   mișcare redusă (html{scroll-behavior:smooth} este global). */
			if (box.scrollIntoView) {
				try {
					box.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
				} catch (e) {
					box.scrollIntoView();
				}
			}
		}

		function clearError() {
			var box = errBox();
			if (!box) { return; }
			if (box.__rovaT) { clearTimeout(box.__rovaT); box.__rovaT = null; }
			box.hidden = true;
			box.textContent = '';
		}

		/* Oglindește assets/theme.js:20337-20341, ca să nu tipărim niciodată
		   „[object Object]”: la validarea destinatarului de card cadou,
		   `description` este un obiect câmp -> mesaj. */
		function describe(body) {
			if (!body) { return ''; }
			var d = body.description;
			if (d && typeof d === 'object') {
				return Object.keys(d).map(function (k) { return k + ': ' + d[k]; }).join(' ');
			}
			if (typeof d === 'string' && d) { return d; }
			if (typeof body.message === 'string' && body.message) { return body.message; }
			return '';
		}

		/* ---------------------------------------------------------------
		   Focus: innerHTML-ul este înlocuit, deci nodul care avea focus dispare.
		   Reținem linia + tipul controlului și le regăsim după re-randare.
		   Dacă linia a fost ștearsă, focusul merge pe titlul paginii, care are
		   tabindex="-1" exact pentru asta.
		   --------------------------------------------------------------- */
		function captureFocus() {
			if (state.design) { return null; }
			var el = document.activeElement;
			if (!el || el === document.body || !root.contains(el)) { return null; }
			var item = closest(el, '[data-rova-item]');
			var key = item ? item.getAttribute('data-key') : null;
			if (el.hasAttribute('data-rova-qty')) { return { key: key, sel: '[data-rova-qty]' }; }
			if (el.hasAttribute('data-rova-remove')) { return { key: key, sel: '[data-rova-remove]' }; }
			if (el.hasAttribute('data-rova-step')) {
				var step = el.getAttribute('data-rova-step') === '1' ? '1' : '-1';
				return { key: key, sel: '[data-rova-step="' + step + '"]' };
			}
			return null;
		}

		function restoreFocus(token) {
			if (!token || state.design) { return; }
			var target = null;
			if (token.key) {
				var rows = root.querySelectorAll('[data-rova-item]');
				for (var i = 0; i < rows.length; i++) {
					if (rows[i].getAttribute('data-key') === token.key) {
						target = rows[i].querySelector(token.sel);
						break;
					}
				}
			}
			if (!target) { target = root.querySelector('.rova-cart__title'); }
			if (!target || !target.focus) { return; }
			try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
		}

		/* ---------------------------------------------------------------
		   Re-randare prin Section Rendering API.
		   Nu respinge NICIODATĂ: lanțul din `change()` se bazează pe asta ca
		   să nu rămână blocat cu `busy = true`.
		   --------------------------------------------------------------- */
		function fetchSection() {
			if (typeof RM !== 'undefined' && RM && typeof RM.fetchSection === 'function') {
				return RM.fetchSection(state.sectionId);
			}
			/* Rezervă locală, identică cu RM.fetchSection (SPEC §2.5). Calea
			   TREBUIE să fie window.location.pathname: id-urile de secțiune se
			   rezolvă față de șablonul căii cerute, deci „/?section_id=main” ar
			   întoarce secțiunea `main` din index.json, alta decât a coșului. */
			var url = window.location.pathname + '?section_id=' + encodeURIComponent(state.sectionId);
			return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
				.then(function (r) {
					if (!r.ok) { throw new Error('HTTP ' + r.status); }
					return r.text();
				})
				.then(function (html) {
					var d = document.createElement('div');
					d.innerHTML = html;
					return d;
				});
		}

		function refresh() {
			if (!state.sectionId) { return Promise.resolve(); }
			var token = captureFocus();
			return fetchSection().then(function (doc) {
				var fresh = doc.querySelector('[data-rova-inner]');
				var live = root.querySelector('[data-rova-inner]');
				if (!fresh || !live) { return; }
				live.innerHTML = fresh.innerHTML;

				/* Clasa `is-empty` și atributele de configurare se pot schimba
				   odată cu conținutul (ultima linie ștearsă, setări editate). */
				var freshRoot = doc.querySelector('[data-rova-section="cart"]');
				if (freshRoot) {
					root.className = freshRoot.className;
					['data-trim-prices', 'data-err-stock', 'data-err-gone', 'data-err-generic'].forEach(function (a) {
						var v = freshRoot.getAttribute(a);
						if (v !== null) { root.setAttribute(a, v); }
					});
					var style = freshRoot.getAttribute('style');
					if (style) { root.setAttribute('style', style); }
				}

				trimPrices();
				restoreFocus(token);
				document.dispatchEvent(new CustomEvent('rova:cart:refresh', { bubbles: true }));
			}).catch(function (e) {
				warn('cart refresh failed', e);
			});
		}

		/* ---------------------------------------------------------------
		   Mutația propriu-zisă
		   --------------------------------------------------------------- */
		function change(key, qty, itemEl) {
			if (state.busy || !key) { return Promise.resolve(); }
			state.busy = true;
			clearError();

			var loaderEl = itemEl ? itemEl.querySelector('[data-rova-item-loader]') : null;
			if (loaderEl) { loaderEl.hidden = false; }
			if (itemEl) { itemEl.setAttribute('aria-busy', 'true'); }

			var requested = parseInt(qty, 10);
			if (isNaN(requested) || requested < 0) { requested = 0; }

			/* Mesajul se afișează abia în ultimul `.then`, după re-randare. */
			var pending = '';

			return fetch(cartRoute() + '/change.js', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json',
					'X-Requested-With': 'XMLHttpRequest'
				},
				body: JSON.stringify({ id: key, quantity: requested })
			})
				.then(function (r) {
					return r.text().then(function (t) {
						var j = null;
						try { j = JSON.parse(t); } catch (e) { j = null; }
						return { ok: r.ok, status: r.status, json: j };
					});
				})
				.then(function (res) {
					if (!res.ok || !res.json) {
						pending = describe(res.json) ||
							txt('data-err-generic', 'Nu am putut actualiza coșul. Te rugăm reîncarcă pagina.');
						return null;
					}

					var cart = res.json;
					var items = cart.items || [];
					var line = null;
					for (var i = 0; i < items.length; i++) {
						if (items[i].key === key) { line = items[i]; break; }
					}
					var actual = line ? line.quantity : 0;

					if (requested > 0 && !line) {
						pending = txt('data-err-gone', 'Produsul nu mai este disponibil și a fost eliminat din coș.');
					} else if (requested === 0 && line) {
						/* Am cerut ștergerea, dar linia este încă în coș. Nu ar trebui
						   să se întâmple; îl tratăm ca eroare generică, nu ca limitare
						   de stoc (nu am cerut nicio cantitate de păstrat). */
						pending = txt('data-err-generic', 'Nu am putut actualiza coșul. Te rugăm reîncarcă pagina.');
					} else if (actual !== requested) {
						if (actual === 0) {
							pending = txt('data-err-gone', 'Produsul nu mai este disponibil și a fost eliminat din coș.');
						} else {
							pending = txt('data-err-stock',
								'Stoc insuficient — am păstrat în coș cantitatea maximă disponibilă: ||qty|| buc.')
								.split('||qty||').join(String(actual));
						}
					} else if (typeof cart.errors === 'string' && cart.errors) {
						/* Semnal secundar, deja localizat de Shopify. Nu este garantat. */
						pending = cart.errors;
					}

					/* Insigna nativă din header. `__rovaSelf` împiedică bucla:
					   onExternal() ar cere altfel un al doilea refresh. */
					root.__rovaSelf = true;
					if (typeof RM !== 'undefined' && RM && typeof RM.cartChange === 'function') {
						RM.cartChange(cart);
					} else {
						document.dispatchEvent(new CustomEvent('theme:cart:change', {
							detail: { cart: cart }, bubbles: true
						}));
					}
					root.__rovaSelf = false;
					return cart;
				})
				.then(function () { return refresh(); })
				.catch(function (e) {
					warn('cart change failed', e);
					pending = pending ||
						txt('data-err-generic', 'Nu am putut actualiza coșul. Te rugăm reîncarcă pagina.');
					return refresh();
				})
				.then(function () {
					state.busy = false;
					/* Inofensiv dacă re-randarea a detașat deja nodurile. */
					if (loaderEl) { loaderEl.hidden = true; }
					if (itemEl && itemEl.removeAttribute) { itemEl.removeAttribute('aria-busy'); }
					showError(pending);
				});
		}

		/* ---------------------------------------------------------------
		   Ascultători — delegați pe rădăcină, deci supraviețuiesc re-randării
		   conținutului din [data-rova-inner].
		   --------------------------------------------------------------- */
		state.onClick = function (e) {
			var rm = closest(e.target, '[data-rova-remove]');
			if (rm) {
				/* Fără JS ancora navighează la /cart/change?id=…&quantity=0 și
				   chiar șterge linia; cu JS preluăm noi. */
				e.preventDefault();
				change(rm.getAttribute('data-rova-remove'), 0, closest(rm, '[data-rova-item]'));
				return;
			}

			var st = closest(e.target, '[data-rova-step]');
			if (!st) { return; }
			e.preventDefault();
			var item = closest(st, '[data-rova-item]');
			var input = item ? item.querySelector('[data-rova-qty]') : null;
			var v = input ? parseInt(input.value, 10) : NaN;
			if (isNaN(v)) { v = 1; }
			var delta = parseInt(st.getAttribute('data-rova-step'), 10);
			if (isNaN(delta)) { return; }
			v = Math.max(0, v + delta);
			change(st.getAttribute('data-key'), v, item);
		};

		state.onChange = function (e) {
			var input = closest(e.target, '[data-rova-qty]');
			if (!input) { return; }
			/* Cât timp o cerere este în curs nu păstrăm o valoare tastată care
			   nu a ajuns niciodată pe server: `defaultValue` este atributul
			   `value` randat de Liquid, adică adevărul serverului. */
			if (state.busy) { input.value = input.defaultValue; return; }
			/* Un câmp GOLIT nu este o cerere de ștergere: parseInt('') este NaN,
			   iar /cart/change.js tratează cantitatea 0 ca ștergere a liniei.
			   Distingem deci „gol / neinterpretabil” de un 0 chiar tastat:
			   primul caz restaurează adevărul serverului (`defaultValue`) și NU
			   trimite nimic. Butoanele de pas fac aceeași distincție (NaN -> 1),
			   deci cele două căi nu se mai contrazic. */
			var v = parseInt(input.value, 10);
			if (isNaN(v)) { input.value = input.defaultValue; return; }
			if (v < 0) { v = 0; }
			change(input.getAttribute('data-rova-qty'), v, closest(input, '[data-rova-item]'));
		};

		state.onKeydown = function (e) {
			if (e.key !== 'Enter' && e.keyCode !== 13) { return; }
			var input = closest(e.target, '[data-rova-qty]');
			if (!input) { return; }
			/* Fără asta, Enter trimite formularul coșului (reîncărcare completă).
			   Rezultatul ar fi corect, dar pierdem poziția din pagină. */
			e.preventDefault();
			if (state.busy) { input.value = input.defaultValue; return; }
			/* Aceeași regulă ca pe calea `change`: Enter pe un câmp gol nu șterge
			   linia, ci readuce valoarea de la server. */
			var v = parseInt(input.value, 10);
			if (isNaN(v)) { input.value = input.defaultValue; return; }
			if (v < 0) { v = 0; }
			change(input.getAttribute('data-rova-qty'), v, closest(input, '[data-rova-item]'));
		};

		root.addEventListener('click', state.onClick);
		root.addEventListener('change', state.onChange);
		root.addEventListener('keydown', state.onKeydown);

		/* Sincronizare cu sertarul Pipeline sau cu orice altă mutație a coșului
		   făcută de altcineva pe aceeași pagină (inclusiv rova-cart-upsell).
		   `__rovaSelf` sare peste propria noastră emisie; temporizatorul
		   comasează perechile change+reload emise la rând. */
		state.onExternal = function () {
			if (root.__rovaSelf) { return; }
			if (state.extTimer) { clearTimeout(state.extTimer); }
			state.extTimer = setTimeout(function () {
				state.extTimer = null;
				refresh();
			}, 60);
		};
		document.addEventListener('theme:cart:change', state.onExternal);
		document.addEventListener('theme:cart:reload', state.onExternal);

		/* Prima randare: Liquid a produs deja prețurile corecte, dar rulăm o
		   dată ca ramura `money_with_currency` (care nu are variantă fără
		   zecimale) să fie și ea curățată. */
		trimPrices();
	},

	destroy: function (root) {
		if (!root || root.nodeType !== 1) { return; }

		var state = root.__rovaCart;
		if (state) {
			if (state.extTimer) { clearTimeout(state.extTimer); state.extTimer = null; }
			if (state.onClick) { root.removeEventListener('click', state.onClick); }
			if (state.onChange) { root.removeEventListener('change', state.onChange); }
			if (state.onKeydown) { root.removeEventListener('keydown', state.onKeydown); }
			if (state.onExternal) {
				document.removeEventListener('theme:cart:change', state.onExternal);
				document.removeEventListener('theme:cart:reload', state.onExternal);
			}
			state.onClick = null;
			state.onChange = null;
			state.onKeydown = null;
			state.onExternal = null;
			state.busy = false;
		}

		var box = root.querySelector('[data-rova-cart-error]');
		if (box && box.__rovaT) { clearTimeout(box.__rovaT); box.__rovaT = null; }

		try { delete root.__rovaCart; } catch (e) { root.__rovaCart = null; }
		try { delete root.__rovaSelf; } catch (e2) { root.__rovaSelf = false; }
		try { delete root.__rovaTeardown; } catch (e3) { root.__rovaTeardown = null; }

		/* Eliberăm marcajul, ca o re-inițializare (editor de teme, randare
		   parțială) să fie posibilă pe același nod. */
		if (root.getAttribute('data-rova-ready') === 'cart') {
			root.removeAttribute('data-rova-ready');
		}
	}
};


  /* ===================================================================
     M.cartupsell  (SPEC 7.2 - sections/rova-cart-upsell.liquid)
     =================================================================== */

/* =========================================================================
   M.cartupsell — sections/rova-cart-upsell.liquid   (SPEC §7.2, §10 pt. 7)
   -------------------------------------------------------------------------
   Rădăcina: [data-rova-section="cartupsell"], adică .rova.rova-cart-upsell.

   CE FACE
     1. Adăugare rapidă în coș de pe un card de upsell: POST /cart/add.js,
        apoi re-randarea PARȚIALĂ a secțiunii, ca produsul adăugat să dispară
        din listă (setarea „Ascunde produsele aflate deja în coș”) și ca un
        produs epuizat între timp să-și piardă butonul.
     2. Sincronizarea restului paginii: `theme:cart:change` (insigna din antet)
        și `theme:cart:reload` (secțiunea de coș + sertarul nativ). Se emit
        AMÂNDOUĂ, ca totul să rămână corect indiferent de `cart_style`.
     3. Reîmprospătare la evenimente externe (`rova:cart:refresh`, plus
        `theme:cart:change` / `theme:cart:reload` emise de alte module), ca
        lista să reflecte un coș modificat din altă parte a paginii.
     4. Aducerea recomandărilor reale, când sursa secțiunii este „Recomandări
        Shopify”: obiectul Liquid `recommendations` este populat DOAR în
        răspunsul rutei de recomandări, deci secțiunea randează întâi rezerva
        (colecție/blocuri) și pune adresa în `data-rova-rec-url`; o singură
        cerere o înlocuiește, și numai dacă răspunsul chiar conține carduri.

   REGULA DE AUR (CORRECTIONS.md, defect 11)
     `data-rova-add` este emis din Liquid DOAR pentru o variantă cu
     `.available` adevărat. JS-ul NU decide nimic despre disponibilitate: dacă
     atributul lipsește sau nu este numeric, nu facem `preventDefault()` și
     ancora navighează normal către pagina produsului. Fără JavaScript,
     secțiunea rămâne complet funcțională (SPEC §8).

   NICIODATĂ `.catch(function () {})`
     Orice respingere de la /cart/add.js este afișată: `description` întors de
     Shopify (deja tradus în limba magazinului), altfel `message`, altfel
     mesajul din setările secțiunii. `description` poate fi un OBIECT
     câmp -> mesaj (validarea destinatarului unui card cadou), exact ca la
     assets/theme.js:20337-20341 — de aceea îl împăturim, ca să nu tipărim
     niciodată „[object Object]”.

   MESAJUL NU SE SCRIE NICIODATĂ ÎN [data-rova-inner]
     `rerender()` face `live.innerHTML = fresh.innerHTML` pe acel nod. Cutia de
     stare este FRATE al lui și mesajul se scrie abia DUPĂ ce re-randarea s-a
     încheiat.

   MIȘCARE REDUSĂ / EDITOR DE TEME
     Modulul nu pornește nicio animație din JS (punctul de încărcare este pur
     CSS și este anulat de `prefers-reduced-motion`), deci nu are ce să
     dezactiveze. În editorul de teme (`Shopify.designMode`) NU aducem automat
     recomandările: editorul re-randează secțiunea la fiecare modificare de
     setare, iar o cerere de rețea pe fiecare tastă ar face panoul să pâlpâie;
     comerciantul vede rezerva reală (colecție/blocuri), care este exact ce vor
     vedea și clienții dacă ruta de recomandări nu întoarce nimic.

   IDEMPOTENȚĂ
     `init` poate fi apelat de oricâte ori: iese imediat dacă rădăcina are deja
     starea internă sau marcajul `data-rova-ready="cartupsell"`. Nu presupune
     nimic despre valoarea pusă de bootloader în `data-rova-ready`: doar
     valoarea proprie („cartupsell”) contează ca „inițializat”.
   ========================================================================= */
M.cartupsell = {

	init: function (root, sectionId) {
		if (!root || root.nodeType !== 1) { return; }
		if (root.__rovaCartUpsell) { return; }
		if (root.getAttribute('data-rova-ready') === 'cartupsell') { return; }
		root.setAttribute('data-rova-ready', 'cartupsell');

		var self = this;
		var state = {
			busy: false,
			extTimer: null,
			msgTimer: null,
			onClick: null,
			onExternal: null,
			onRefresh: null
		};
		root.__rovaCartUpsell = state;

		/* Punte către bootloader-ul din SPEC §2.4, care apelează
		   root.__rovaTeardown() la `shopify:section:unload`. */
		root.__rovaTeardown = function () { self.destroy(root); };

		var id = sectionId || root.getAttribute('data-section-id') || '';
		var trimOn = root.getAttribute('data-rova-trim-prices') === 'true';

		/* ---------------------------------------------------------------
		   Ajutoare locale. RM (SPEC §2.5) este definit în același IIFE;
		   rezervele există ca modulul să rămână funcțional dacă ordinea de
		   asamblare a fișierului se schimbă. Logica este identică.
		   --------------------------------------------------------------- */
		function routes() {
			return (window.theme && window.theme.routes) ? window.theme.routes : {};
		}

		function cartUrl() {
			var r = routes();
			return (typeof r.cart === 'string' && r.cart) ? r.cart : '/cart';
		}

		function addUrl() {
			var r = routes();
			return (typeof r.cart_add_url === 'string' && r.cart_add_url) ? r.cart_add_url : '/cart/add';
		}

		function fetchSection(secId) {
			if (typeof RM !== 'undefined' && RM && typeof RM.fetchSection === 'function') {
				return RM.fetchSection(secId);
			}
			/* window.location.pathname, NU routes.root_url: id-urile de secțiune
			   se rezolvă față de șablonul căii cerute (SPEC §2.5). */
			var url = window.location.pathname + '?section_id=' + encodeURIComponent(secId);
			return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
				.then(function (r) {
					if (!r.ok) { throw new Error(r.status); }
					return r.text();
				})
				.then(function (html) {
					var d = document.createElement('div');
					d.innerHTML = html;
					return d;
				});
		}

		function cartChange(cart) {
			if (typeof RM !== 'undefined' && RM && typeof RM.cartChange === 'function') {
				RM.cartChange(cart);
				return;
			}
			document.dispatchEvent(new CustomEvent('theme:cart:change', {
				detail: { cart: cart },
				bubbles: true
			}));
		}

		function trimPrices() {
			if (!trimOn) { return; }
			if (typeof RM !== 'undefined' && RM && typeof RM.trimZeros === 'function') {
				RM.trimZeros(root);
				return;
			}
			var nodes = root.querySelectorAll('[data-rova-trim], [data-button-price]');
			Array.prototype.forEach.call(nodes, function (node) {
				var was = node.textContent;
				var now = was.replace(/([0-9])[.,]00(?![0-9])/g, '$1');
				if (now !== was) { node.textContent = now; }
			});
		}

		/* ---------------------------------------------------------------
		   Cutia de stare (erori + confirmare). Un singur nod aria-live:
		   două containere ar face cititorul de ecran să anunțe de două ori.
		   --------------------------------------------------------------- */
		function box() { return root.querySelector('[data-rova-up-error]'); }

		function attr(name, fallback) {
			var v = root.getAttribute(name);
			return (v && v.length) ? v : fallback;
		}

		function clearMsg() {
			var b = box();
			if (state.msgTimer) { clearTimeout(state.msgTimer); state.msgTimer = null; }
			if (!b) { return; }
			b.hidden = true;
			b.textContent = '';
			b.classList.remove('rova-up__error--ok');
		}

		function showMsg(msg, isOk) {
			var b = box();
			if (!b) { return; }
			if (!msg) { return; }
			if (state.msgTimer) { clearTimeout(state.msgTimer); state.msgTimer = null; }
			b.textContent = msg;
			if (isOk) { b.classList.add('rova-up__error--ok'); }
			else { b.classList.remove('rova-up__error--ok'); }
			b.hidden = false;
			state.msgTimer = setTimeout(function () {
				state.msgTimer = null;
				b.hidden = true;
				b.textContent = '';
				b.classList.remove('rova-up__error--ok');
			}, isOk ? 5000 : 8000);
		}

		/* Oglindește assets/theme.js:20328-20345. */
		function describe(body) {
			if (!body) { return ''; }
			var d = body.description;
			if (d && typeof d === 'object') {
				return Object.keys(d).map(function (k) { return k + ': ' + d[k]; }).join(' ');
			}
			if (typeof d === 'string' && d) { return d; }
			if (typeof body.message === 'string' && body.message) { return body.message; }
			return '';
		}

		function fallbackMsg() {
			return attr('data-err-add', 'Nu am putut adăuga produsul în coș. Te rugăm încearcă din nou.');
		}

		/* ---------------------------------------------------------------
		   Re-randare parțială: se schimbă DOAR innerHTML-ul lui
		   [data-rova-inner], niciodată rădăcina (care ține atributele de
		   configurare, cutia de stare și marcajul data-rova-ready).
		   --------------------------------------------------------------- */
		function swap(doc) {
			if (!doc) { return false; }
			var fresh = doc.querySelector('[data-rova-inner]');
			var live = root.querySelector('[data-rova-inner]');
			if (!fresh) { return false; }
			if (!live) { return false; }
			live.innerHTML = fresh.innerHTML;
			trimPrices();
			return true;
		}

		function rerender() {
			if (!id) { return Promise.resolve(false); }
			return fetchSection(id)
				.then(function (doc) { return swap(doc); })
				.catch(function (e) {
					if (window.console) { console.warn('[rova] cart-upsell rerender failed', e); }
					return false;
				});
		}

		/* După o re-randare, nodul apăsat poate să nu mai existe (produsul a
		   intrat în coș și a fost ascuns). Ducem focalizarea pe următorul
		   buton din listă, ca navigarea cu tastatura să nu cadă pe <body>. */
		function restoreFocus(previous) {
			if (!previous) { return; }
			if (document.activeElement !== document.body) { return; }
			if (root.contains(previous)) {
				if (typeof previous.focus === 'function') { previous.focus(); }
				return;
			}
			var next = root.querySelector('[data-rova-inner] .rova-up__add');
			if (next && typeof next.focus === 'function') { next.focus(); }
		}

		/* ---------------------------------------------------------------
		   1. Adăugare rapidă în coș.
		   --------------------------------------------------------------- */
		function findAdd(node) {
			var el = node;
			while (el) {
				if (el === root) { return null; }
				if (el.nodeType === 1) {
					if (el.hasAttribute('data-rova-add')) { return el; }
				}
				el = el.parentNode;
			}
			return null;
		}

		state.onClick = function (e) {
			/* Click cu modificator / rotița mouse-ului: lăsăm browserul să
			   deschidă pagina produsului într-o filă nouă. */
			if (e.button) { return; }
			if (e.metaKey) { return; }
			if (e.ctrlKey) { return; }
			if (e.shiftKey) { return; }
			if (e.altKey) { return; }
			if (e.defaultPrevented) { return; }

			var btn = findAdd(e.target);
			if (!btn) { return; }

			var raw = btn.getAttribute('data-rova-add');
			var variantId = parseInt(raw, 10);
			/* Fără id valid nu interceptăm nimic: ancora duce la pagina produsului. */
			if (!variantId) { return; }

			e.preventDefault();
			if (state.busy) { return; }
			if (btn.getAttribute('aria-busy') === 'true') { return; }

			state.busy = true;
			btn.setAttribute('aria-busy', 'true');
			btn.classList.add('is-loading');
			clearMsg();

			var failMsg = '';

			fetch(addUrl() + '.js', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json',
					'X-Requested-With': 'XMLHttpRequest'
				},
				body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
			})
				.then(function (r) {
					/* Citim textul o singură dată și abia apoi încercăm JSON:
					   un 422 poate întoarce și un corp care nu este JSON. */
					return r.text().then(function (t) {
						var j = null;
						try { j = JSON.parse(t); } catch (err) { j = null; }
						return { ok: r.ok, status: r.status, json: j };
					});
				})
				.then(function (res) {
					if (!res.ok) {
						/* 422 stoc epuizat / peste stoc, 404 variantă inexistentă. */
						failMsg = describe(res.json) || fallbackMsg();
						var err = new Error(failMsg);
						err.__rovaHandled = true;
						throw err;
					}
					return fetch(cartUrl() + '.js', { headers: { 'Accept': 'application/json' } })
						.then(function (r) { return r.json(); });
				})
				.then(function (cart) {
					/* Marcăm propria emisie, ca ascultătorii de mai jos să nu
					   ceară încă o dată aceeași secțiune. */
					root.__rovaSelf = true;
					cartChange(cart);
					document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
					root.__rovaSelf = false;
					return rerender().then(function () {
						restoreFocus(btn);
						showMsg(attr('data-ok-add', ''), true);
					});
				})
				.catch(function (err) {
					root.__rovaSelf = false;
					if (window.console) { console.warn('[rova] cart-upsell add failed', err); }
					/* Re-randăm ca un card devenit indisponibil să-și piardă butonul,
					   apoi afișăm mesajul — într-un nod pe care re-randarea nu îl atinge. */
					return rerender().then(function () {
						restoreFocus(btn);
						showMsg(failMsg || fallbackMsg(), false);
					});
				})
				.then(function () {
					state.busy = false;
					/* Inofensiv dacă re-randarea a detașat deja nodul. */
					btn.removeAttribute('aria-busy');
					btn.classList.remove('is-loading');
				});
		};

		root.addEventListener('click', state.onClick);

		/* ---------------------------------------------------------------
		   2. Sincronizare cu restul paginii.
		   `theme:cart:change` / `theme:cart:reload` sunt emise de M.cart și de
		   tema gazdă; le ascultăm ca lista să reflecte un coș modificat din
		   altă parte (o cantitate scăzută la 0 trebuie să readucă produsul în
		   upsell). Propriile noastre emisii sunt sărite prin `__rovaSelf`, iar
		   perechile change+reload sunt comasate cu o temporizare scurtă.
		   --------------------------------------------------------------- */
		state.onExternal = function () {
			if (root.__rovaSelf) { return; }
			if (state.busy) { return; }
			if (state.extTimer) { clearTimeout(state.extTimer); }
			state.extTimer = setTimeout(function () {
				state.extTimer = null;
				rerender();
			}, 120);
		};

		state.onRefresh = state.onExternal;

		document.addEventListener('rova:cart:refresh', state.onRefresh);
		document.addEventListener('theme:cart:change', state.onExternal);
		document.addEventListener('theme:cart:reload', state.onExternal);

		/* ---------------------------------------------------------------
		   3. Recomandările reale (o singură cerere, la inițializare).
		   `data-rova-rec-url` este emis de Liquid DOAR când sursa este
		   „Recomandări Shopify” ȘI randarea curentă nu este deja răspunsul
		   rutei de recomandări — deci bucla este imposibilă. Înlocuim numai
		   dacă răspunsul chiar conține carduri, altfel rezerva rămâne.
		   --------------------------------------------------------------- */
		var recUrl = root.getAttribute('data-rova-rec-url');
		if (!recUrl) { return; }
		if (window.Shopify && window.Shopify.designMode) { return; }

		fetch(recUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
			.then(function (r) {
				if (!r.ok) { throw new Error(r.status); }
				return r.text();
			})
			.then(function (html) {
				var d = document.createElement('div');
				d.innerHTML = html;
				if (!d.querySelector('[data-rova-up-item]')) { return; }
				swap(d);
			})
			.catch(function (e) {
				if (window.console) { console.warn('[rova] cart-upsell recommendations failed', e); }
			});
	},

	destroy: function (root) {
		if (!root || root.nodeType !== 1) { return; }

		var state = root.__rovaCartUpsell;
		if (state) {
			if (state.extTimer) { clearTimeout(state.extTimer); state.extTimer = null; }
			if (state.msgTimer) { clearTimeout(state.msgTimer); state.msgTimer = null; }
			if (state.onClick) { root.removeEventListener('click', state.onClick); }
			if (state.onRefresh) { document.removeEventListener('rova:cart:refresh', state.onRefresh); }
			if (state.onExternal) {
				document.removeEventListener('theme:cart:change', state.onExternal);
				document.removeEventListener('theme:cart:reload', state.onExternal);
			}
			state.onClick = null;
			state.onExternal = null;
			state.onRefresh = null;
			state.busy = false;
		}

		try { delete root.__rovaCartUpsell; } catch (e) { root.__rovaCartUpsell = null; }
		try { delete root.__rovaSelf; } catch (e2) { root.__rovaSelf = null; }
		try { delete root.__rovaTeardown; } catch (e3) { root.__rovaTeardown = null; }

		/* Eliberăm marcajul, ca o re-inițializare (editor de teme, randare
		   parțială) să fie posibilă pe același nod. */
		if (root.getAttribute('data-rova-ready') === 'cartupsell') {
			root.removeAttribute('data-rova-ready');
		}
	}
};


  /* ===================================================================
     M.grid  (SPEC 4.2 - sections/rova-product-grid.liquid)
     =================================================================== */

/* =========================================================================
   M.grid — sections/rova-product-grid.liquid            (SPEC §4.2, §10 pt. 8)
   -------------------------------------------------------------------------
   Rădăcina: [data-rova-section="grid"], adică .rova.rova-product-grid.

   CE FACE
     1. Taie zecimalele nule din prețuri ("219,00 lei" -> "219 lei") când
        secțiunea are `data-rova-trim-prices="true"`.
        Liquid produce deja șirul corect prin `money_without_trailing_zeros`
        (snippets/rova-money.liquid), deci pasul este redundant la prima
        randare — DAR devine necesar pentru orice nod de preț rescris ulterior
        din JS: themeVendor.themeCurrency.formatMoney() are două zecimale
        hardcodate (vendor.js:470) și reintroduce ",00".
        De aceea urmărim și mutațiile din [data-rova-inner] și retăiem.
     2. Sincronizează zoom-ul de card cu `prefers-reduced-motion`, în cazurile
        în care utilizatorul schimbă preferința fără să reîncarce pagina
        (CSS-ul acoperă deja randarea inițială, prin --rova-card-zoom).

   CE NU FACE, DELIBERAT
     * Nu atinge layoutul: grila, proporțiile și punctele de rupere sunt 100% CSS.
     * Nu face niciun `document.querySelector` — totul este interogat în `root`
       (regula din SPEC §2.4).
     * Nu adaugă listeners pe `document` sau `window` în afara MediaQueryList,
       care este oricum eliberat în destroy().
     * Shopify.designMode nu are nevoie de tratament special aici: editorul
       re-randează secțiunea întreagă, iar `shopify:section:load` reinițializează
       modulul; `destroy()` eliberează observatorul și temporizatorul înainte ca
       nodul vechi să fie aruncat. Îl consultăm doar ca să nu urmărim mutații
       inutile atunci când tăierea prețurilor este oprită.

   IDEMPOTENȚĂ
     `init` poate fi apelat de oricâte ori: iese imediat dacă rădăcina are deja
     starea internă sau marcajul `data-rova-ready="grid"`. Nu presupune nimic
     despre valoarea pe care bootloader-ul a pus-o în `data-rova-ready`: doar
     valoarea proprie ("grid") contează ca „inițializat”.
   ========================================================================= */
M.grid = {

	init: function (root) {
		if (!root || root.nodeType !== 1) { return; }
		if (root.__rovaGrid) { return; }
		if (root.getAttribute('data-rova-ready') === 'grid') { return; }
		root.setAttribute('data-rova-ready', 'grid');

		var self = this;
		var state = {
			timer: null,
			observer: null,
			mq: null,
			onMq: null,
			onLoad: null,
			target: null
		};
		root.__rovaGrid = state;

		/* Punte către bootloader-ul din SPEC §2.4, care apelează
		   root.__rovaTeardown() la `shopify:section:unload`. */
		root.__rovaTeardown = function () { self.destroy(root); };

		var ZOOM_CLASS = 'rova-product-grid--zoom';
		var trimOn = root.getAttribute('data-rova-trim-prices') === 'true';
		var zoomWanted = root.getAttribute('data-rova-zoom') === 'true';

		/* ---------------------------------------------------------------
		   1. Mișcare redusă (SPEC §8).
		   CSS-ul anulează deja zoom-ul prin @media, dar clasa de pe rădăcină
		   trebuie să reflecte realitatea și pentru codul/temele care o citesc.
		   Nu adăugăm NICIODATĂ clasa dacă merchantul a dezactivat setarea.
		   --------------------------------------------------------------- */
		if (window.matchMedia) {
			state.mq = window.matchMedia('(prefers-reduced-motion: reduce)');
			state.onMq = function () {
				if (!zoomWanted) { return; }
				if (state.mq.matches) { root.classList.remove(ZOOM_CLASS); }
				else { root.classList.add(ZOOM_CLASS); }
			};
			state.onMq();
			if (state.mq.addEventListener) { state.mq.addEventListener('change', state.onMq); }
			else if (state.mq.addListener) { state.mq.addListener(state.onMq); }
		}

		/* ---------------------------------------------------------------
		   2. Rotația automată a produselor.
		   Montată ÎNAINTE de return-ul timpuriu de la tăierea zecimalelor, ca
		   să funcționeze și când tăierea e oprită. Pornește doar la 2+ seturi și
		   NU pornește sub „prefers-reduced-motion” (o mișcare declanșată singură).
		   Pauză cât mouse-ul e pe grilă și cât tab-ul e ascuns.
		   --------------------------------------------------------------- */
		if (root.getAttribute('data-rova-rotate') === 'true') {
			var rPages = parseInt(root.getAttribute('data-rova-pages'), 10) || 1;
			var rSecs = parseInt(root.getAttribute('data-rova-rotate-secs'), 10) || 60;
			var rList = root.querySelector('.rova-grid__list');
			var rReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			if (rPages >= 2 && rList && !rReduce) {
				var rActive = 0;
				state.rPaused = false;
				state.rList = rList;
				state.rFade = null;
				state.rOnRoot = root;
				state.rEnter = function () { state.rPaused = true; };
				state.rLeave = function () { state.rPaused = false; };
				root.addEventListener('mouseenter', state.rEnter);
				root.addEventListener('mouseleave', state.rLeave);
				state.rTimer = setInterval(function () {
					if (state.rPaused || document.hidden) { return; }
					rList.classList.add('is-rota-fading');
					state.rFade = setTimeout(function () {
						rActive = (rActive + 1) % rPages;
						root.setAttribute('data-rova-active', String(rActive));
						rList.classList.remove('is-rota-fading');
					}, 400);
				}, rSecs * 1000);
			}
		}

		/* ---------------------------------------------------------------
		   3. Tăierea zecimalelor nule.
		   --------------------------------------------------------------- */
		if (!trimOn) { return; }

		state.target = root.querySelector('[data-rova-inner]') || root;

		function trim() {
			/* RM.trimZeros (SPEC §2.5) este definit în același IIFE. Rezerva
			   locală există ca modulul să rămână funcțional dacă ordinea de
			   asamblare a fișierului se schimbă. Regexul este identic. */
			if (typeof RM !== 'undefined' && RM && typeof RM.trimZeros === 'function') {
				RM.trimZeros(root);
				return;
			}
			var nodes = root.querySelectorAll('[data-rova-trim], [data-button-price]');
			Array.prototype.forEach.call(nodes, function (node) {
				var was = node.textContent;
				var now = was.replace(/([0-9])[.,]00(?![0-9])/g, '$1');
				if (now !== was) { node.textContent = now; }
			});
		}

		trim();

		/* Retăiere la orice rescriere a conținutului grilei (aplicații de
		   recenzii, valutare multiple, quick-add etc.).
		   `disconnect()` golește și coada de înregistrări, deci scrierile
		   făcute de `trim()` nu se pot reintroduce în observator: nicio buclă. */
		if (!window.MutationObserver) { return; }

		function observe() {
			state.observer.observe(state.target, {
				childList: true,
				subtree: true,
				characterData: true
			});
		}

		function run() {
			state.timer = null;
			if (!state.observer) { trim(); return; }
			state.observer.disconnect();
			trim();
			observe();
		}

		state.observer = new MutationObserver(function () {
			if (state.timer) { return; }
			state.timer = setTimeout(run, 80);
		});
		observe();
	},

	destroy: function (root) {
		if (!root || root.nodeType !== 1) { return; }

		var state = root.__rovaGrid;
		if (state) {
			if (state.timer) { clearTimeout(state.timer); state.timer = null; }
			if (state.rTimer) { clearInterval(state.rTimer); state.rTimer = null; }
			if (state.rFade) { clearTimeout(state.rFade); state.rFade = null; }
			if (state.rOnRoot && state.rEnter) { state.rOnRoot.removeEventListener('mouseenter', state.rEnter); }
			if (state.rOnRoot && state.rLeave) { state.rOnRoot.removeEventListener('mouseleave', state.rLeave); }
			if (state.rList) { state.rList.classList.remove('is-rota-fading'); }
			state.rEnter = null; state.rLeave = null; state.rList = null; state.rOnRoot = null;
			if (state.observer) { state.observer.disconnect(); state.observer = null; }
			if (state.mq && state.onMq) {
				if (state.mq.removeEventListener) { state.mq.removeEventListener('change', state.onMq); }
				else if (state.mq.removeListener) { state.mq.removeListener(state.onMq); }
			}
			state.mq = null;
			state.onMq = null;
			state.target = null;
		}

		try { delete root.__rovaGrid; } catch (e) { root.__rovaGrid = null; }
		try { delete root.__rovaTeardown; } catch (e) { root.__rovaTeardown = null; }

		/* Eliberăm marcajul, ca o re-inițializare (editor de teme, randare
		   parțială) să fie posibilă pe același nod. */
		if (root.getAttribute('data-rova-ready') === 'grid') {
			root.removeAttribute('data-rova-ready');
		}
	}
};


  /* =======================================================================
     ALIASURI DE REGISTRU
     -----------------------------------------------------------------------
     Secțiunile nu folosesc un singur stil de nume în `data-rova-section`:
     patru dintre ele au fost scrise cu numele scurt al modulului
     („grid”, „pdp”, „cart”, „cartupsell”, dovedit de antetele din css-parts și
     de aserțiunile QA din CORRECTIONS), iar restul cu numele complet al
     fișierului de secțiune („rova-hero”, „rova-why”, …). Ca să nu depindem de
     ghicit, fiecare modul este înregistrat sub AMBELE nume. Sunt referințe către
     același obiect, deci nu există stare duplicată și nici identificator
     duplicat; `boot()` și `unboot()` găsesc modulul oricare dintre cele două
     valori ar fi emis Liquid.
     ======================================================================= */

  M['rova-review-stack'] = M.reviewstack;
  M['rova-faq'] = M.faq;
  M['rova-product'] = M.pdp;
  M['rova-product-grid'] = M.grid;
  M['rova-cart'] = M.cart;
  M['rova-cart-upsell'] = M.cartupsell;

  /* =======================================================================
     PORNIRE + EXPORT
     ======================================================================= */

  ready(function () { boot(document); });

  /* Singurul global în afara `__rovaBooted`. `boot` este expus ca să poată fi
     rulat manual după o injecție de marcaj făcută de o aplicație terță;
     `modules` este expus doar pentru diagnosticare în consolă. */
  window.Rova = { boot: boot, unboot: unboot, modules: M, helpers: RM };
})();
